// fieldEditors — platform-agnostic, widget-per-field editors for the
// per-event override surface. Each editor takes the *resolved* (inherited)
// value plus its source and renders a real control (toggle / number / rate
// ladder / image picker / …) — never a raw JSON textarea, except the
// explicit `raw` fallback for shapes without a dedicated editor.
//
// The contract every editor honors:
//   value   — the resolved value (venue → template → event → canonical merge)
//   source  — 'event' (overridden/pinned) | 'venue' | 'template' | 'canonical' | 'default'
//   onSave(v)  — persist v as the event-level override for this field
//   onReset()  — drop the event-level override, fall back to inherited
//   busy       — shell-owned in-flight flag; disables controls + guards double-submit
//
// `<OverrideField>` is the dispatcher the shell renders per catalog entry; it
// picks the right editor from `meta.kind` (+ `meta.key` for the dedicated
// collection editors) and wires onSave/onReset to the shell's patch().

import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api.js';
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core';
import { groupIntoLadders, applyOrder, nextTierTemplate, blankTierTemplate, liveRatesToTiers } from '../../lib/rateLadder.js';
import { TimeField, DateField, DateTimeField, DurationField } from '../../lib/timeFields.jsx';

// Uploadcare direct-upload (same public key as the Universe seller portal).
const UPLOADCARE_PUB_KEY = '6983c6f19fcba1adfe3d';
async function uploadcareUpload(file) {
  const fd = new FormData();
  fd.append('UPLOADCARE_PUB_KEY', UPLOADCARE_PUB_KEY);
  fd.append('UPLOADCARE_STORE', '1');
  fd.append('file', file);
  const r = await fetch('https://upload.uploadcare.com/base/', { method: 'POST', body: fd });
  if (!r.ok) throw new Error(`uploadcare upload failed: ${r.status}`);
  const d = await r.json();
  if (!d.file) throw new Error('uploadcare returned no file uuid');
  return d.file;
}

// === inheritance badge =====================================================
// 'event' means the field is pinned (overridden) for this event and will NOT
// follow future template/venue changes. Everything else is inherited; we name
// the layer it came from so ops can see where the value lives.
export function sourceLabel(source) {
  return ({
    event: 'Custom for this event',
    venue: 'From venue defaults',
    template: 'From the residency template',
    canonical: 'From event data',
    default: 'Platform default',
  }[source] || 'Inherited');
}

// Small key explaining the source badges — drop it at the top of the editor
// so the badge words aren't a guessing game.
export function SourceLegend({ className = '' }) {
  return (
    <div className={`text-[10px] text-muted-foreground leading-relaxed ${className}`}>
      <span className="font-medium text-foreground/70">Where each value comes from:</span>{' '}
      <span className="text-amber-700 dark:text-amber-300">Custom for this event</span> (pinned, won't follow template changes) ·{' '}
      <span className="text-indigo-700 dark:text-indigo-300">From the residency template</span> ·{' '}
      <span className="text-slate-600 dark:text-slate-300">From venue defaults</span> ·{' '}
      <span className="text-emerald-700 dark:text-emerald-300">From event data</span>
    </div>
  );
}

function sourceTone(source) {
  return ({
    event:     'bg-amber-500/15 text-amber-700 dark:text-amber-300',
    venue:     'bg-slate-500/10 text-slate-600 dark:text-slate-300',
    template:  'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300',
    canonical: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
    default:   'bg-muted text-muted-foreground',
  }[source] || 'bg-muted text-muted-foreground');
}

export function SourceBadge({ source }) {
  const overridden = source === 'event';
  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded ${sourceTone(source)}`}
      title={overridden
        ? "Overridden for this event, won't follow template/venue changes"
        : `Inherited (${source}), edit to override just this event`}
    >
      {sourceLabel(source)}
    </span>
  );
}

// === promote-to-tier buttons ===============================================
// Which editor keys can be promoted into which inheritance tier. MUST mirror
// VENUE_PROMOTE_KEYS / TEMPLATE_PROMOTE_KEYS in server/routes/universe.js
// (promote-field) — keep in sync.
const EXTRA_PROMOTABLE = [
  'privacy', 'getTicketsText', 'socialButtons', 'dateDisplayOption', 'hiddenDate',
  'hideSoldOutRatesAtCheckout', 'showQuantityFilter', 'showTicketTypeFilter',
  'showTimeOfDayFilter', 'needsApproval', 'allowWaitlist', 'hostPaysCommission',
  'availableCountries', 'noneditableOffset', 'nontransferrableOffset',
  'allowedMoveOrderOffset', 'hideFromWidgets', 'hiddenFromTicketmaster',
  'hideAttractionsFromTicketmaster', 'fanReminder', 'emailReminders',
  'emailConfirmations', 'sendTicketPdfInEmails', 'sendPurchaseNotifications',
  'sendPurchaseNotificationsImmediately', 'eventNotificationThreshold',
  'eventNotificationThresholdEnabled', 'googleAnalytics4Id', 'googleAnalyticsId',
  'googleAnalyticsEcommerce', 'mailchimpApiKey', 'mailchimpListId',
  'attractionsMainTitle', 'attractionsHeadlinersTitle', 'attractionsLineupTitle',
];
export const VENUE_PROMOTABLE = new Set([
  'address', 'latitude', 'longitude', 'venueName', 'categoryId',
  'transactionCurrency', 'settlementCurrency', 'userId', 'refundPolicy',
  'allInPricing', 'ageLimit', 'maxQuantity', 'urgencyIndicatorEnabled',
  'urgencyIndicatorThreshold', 'endSalesOffset', 'endSalesOffsetFromEventEnd',
  'taxes', 'fees', 'wrstbndIntegration', 'ticketmasterVenueId',
  'privateNote', 'accessibilityDescription', 'contactDetails',
  ...EXTRA_PROMOTABLE,
]);
export const TEMPLATE_PROMOTABLE = new Set([
  'descriptionHtml', 'privateNote', 'accessibilityDescription', 'contactDetails',
  'ageLimit', 'capacity', 'hostFields', 'rateUpgrades', 'emailTemplates',
  'customCheckoutLink', 'addOns', 'discountCodes',
  ...EXTRA_PROMOTABLE,
]);

// Tiny "save this value up a tier" buttons next to the source badge — venue
// defaults always (when the key has a venue slot), template only when the
// event actually uses one. Promotes OUR value when set, else the live value.
function PromoteButtons({ promote, busy }) {
  if (!promote) return null;
  return promote.targets.map((t) => (
    <button
      key={t}
      onClick={() => { if (!busy) promote.run(t); }}
      disabled={busy}
      aria-disabled={busy}
      className="text-[10px] px-1.5 py-0.5 border border-input rounded text-muted-foreground hover:bg-accent disabled:opacity-50 whitespace-nowrap"
      title={t === 'venue'
        ? 'Save this value into the venue defaults; applies to ALL events at this venue'
        : 'Save this value into the residency template, applies to all events using it'}
    >{t === 'venue' ? '→ venue' : '→ template'}</button>
  ));
}

// A small "reset to template" affordance, shown only when the field is pinned.
function ResetButton({ onReset, busy, label = 'reset' }) {
  return (
    <button
      onClick={() => { if (!busy) onReset(); }}
      disabled={busy}
      aria-disabled={busy}
      className="text-[10px] px-1.5 py-0.5 border border-input rounded text-muted-foreground hover:bg-accent disabled:opacity-50"
      title="Drop the event-level override and inherit from template/venue"
    >{label}</button>
  );
}

// === scalar fields (bool / number / text) ==================================

// Booleans get a direct toggle — flipping it is an intentional pin, so no
// separate edit/save step. Reset un-pins. When OUR config holds no value the
// toggle shows the platform's LIVE state (the effective value — an unset
// toggle rendering "off" while the listing is on would lie); flipping it
// still writes an explicit override.
function BoolField({ label, fieldKey, value, source, liveValue, onSave, onReset, busy, promote, staged }) {
  const explicit = value !== undefined && value !== null && value !== '';
  const checked = explicit ? (value === true || value === 'true') : (liveValue === true || liveValue === 'true');
  return (
    <FieldFrame label={label} fieldKey={fieldKey} source={source} onReset={onReset} busy={busy} promote={promote} staged={staged}>
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          disabled={busy}
          onChange={(e) => { if (!busy) onSave(e.target.checked); }}
        />
        <span className="text-muted-foreground">{checked ? 'on' : 'off'}</span>
      </label>
      <LiveLine liveValue={liveValue} value={explicit ? value : liveValue} />
    </FieldFrame>
  );
}

function ScalarField({ label, fieldKey, value, source, kind, liveValue, onSave, onReset, busy, promote, staged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // No value in our tiers → the platform's live value IS the value; show it,
  // never a blank. Saving it pins an override (explicit action).
  const effective = (value === undefined || value === null || value === '') ? liveValue : value;
  const start = () => { setDraft(effective == null ? '' : String(effective)); setEditing(true); };
  const commit = () => {
    if (busy) return;
    let parsed = draft;
    if (kind === 'number') {
      if (draft.trim() === '') { parsed = null; }
      else { const n = Number(draft); parsed = Number.isNaN(n) ? draft : n; }
    }
    onSave(parsed);
    setEditing(false);
  };
  return (
    <FieldFrame
      label={label}
      fieldKey={fieldKey}
      source={source}
      onReset={onReset}
      busy={busy}
      editing={editing}
      onEdit={start}
      onCancel={() => setEditing(false)}
      onCommit={commit}
      promote={promote}
    >
      {editing ? (
        <input
          type={kind === 'number' ? 'number' : kind === 'datetime' ? 'datetime-local' : 'text'}
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
          className="w-full px-2 py-1 border border-input rounded-md bg-background text-xs"
        />
      ) : (
        <>
          <span className="break-all">
            {effective == null || effective === ''
              ? <em className="text-muted-foreground">—</em>
              : String(effective)}
          </span>
          <LiveLine liveValue={liveValue} value={effective} />
        </>
      )}
    </FieldFrame>
  );
}

// Shared chrome for scalar/text/bool fields: label + key + source badge +
// edit/save/cancel/reset buttons + the control (children).
// Amber dot = this field has a staged local edit not yet pushed to Universe.
function StagedDot({ staged }) {
  if (!staged) return null;
  return <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" title="Staged locally, not yet pushed to Universe" />;
}

function FieldFrame({ label, fieldKey, source, onReset, busy, editing, onEdit, onCancel, onCommit, promote, staged, children }) {
  const overridden = source === 'event';
  return (
    <div className="grid grid-cols-[180px_1fr_auto] gap-2 items-start py-1.5 border-t border-border first:border-0 text-xs">
      <div className="pt-1">
        <div className="font-medium">{label || fieldKey}</div>
        <div className="text-[10px] text-muted-foreground font-mono">{fieldKey}</div>
      </div>
      <div className="pt-0.5">{children}</div>
      <div className="flex items-center gap-1.5 pt-0.5 flex-shrink-0">
        <StagedDot staged={staged} />
        <SourceBadge source={source} />
        {!editing && <PromoteButtons promote={promote} busy={busy} />}
        {onEdit && !editing && (
          <button
            onClick={() => { if (!busy) onEdit(); }}
            disabled={busy}
            aria-disabled={busy}
            className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-50"
          >edit</button>
        )}
        {editing && (
          <>
            <button onClick={onCommit} disabled={busy} aria-disabled={busy} className="text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded disabled:opacity-50">{busy ? 'saving…' : 'save'}</button>
            <button onClick={onCancel} disabled={busy} className="text-[10px] px-1.5 py-0.5 border border-input rounded">cancel</button>
          </>
        )}
        {!editing && overridden && onReset && <ResetButton onReset={onReset} busy={busy} />}
      </div>
    </div>
  );
}

// === time / date / duration fields =========================================
// Ours-if-set else live ('' counts as unset — ?? alone misses it).
const effOr = (value, liveValue) => ((value === undefined || value === null || value === '') ? liveValue : value);
// Direct-persist rows (no edit/save cycle — changing the picker pins the
// value, like BoolField). They wrap the shared widgets from lib/timeFields.

function TimeFieldRow({ label, fieldKey, value, source, liveValue, onSave, onReset, busy, format, promote, staged }) {
  return (
    <FieldFrame label={label} fieldKey={fieldKey} source={source} onReset={onReset} busy={busy} promote={promote} staged={staged}>
      <TimeField value={effOr(value, liveValue)} format={format} disabled={busy} onChange={(v) => { if (!busy) onSave(v); }} />
      <LiveLine liveValue={liveValue} value={effOr(value, liveValue)} />
    </FieldFrame>
  );
}

function DateFieldRow({ label, fieldKey, value, source, liveValue, onSave, onReset, busy, promote, staged }) {
  return (
    <FieldFrame label={label} fieldKey={fieldKey} source={source} onReset={onReset} busy={busy} promote={promote} staged={staged}>
      <DateField value={effOr(value, liveValue)} disabled={busy} onChange={(v) => { if (!busy) onSave(v); }} />
      <LiveLine liveValue={liveValue} value={effOr(value, liveValue)} />
    </FieldFrame>
  );
}

function DateTimeFieldRow({ label, fieldKey, value, source, liveValue, tz, onSave, onReset, busy, promote, staged }) {
  return (
    <FieldFrame label={label} fieldKey={fieldKey} source={source} onReset={onReset} busy={busy} promote={promote} staged={staged}>
      <DateTimeField value={effOr(value, liveValue)} tz={tz} disabled={busy} onChange={(v) => { if (!busy) onSave(v); }} />
      <LiveLine liveValue={liveValue} value={effOr(value, liveValue)} />
    </FieldFrame>
  );
}

function DurationFieldRow({ label, fieldKey, value, source, liveValue, relLabel, onSave, onReset, busy, promote, staged }) {
  return (
    <FieldFrame label={label} fieldKey={fieldKey} source={source} onReset={onReset} busy={busy} promote={promote} staged={staged}>
      <DurationField seconds={effOr(value, liveValue)} relLabel={relLabel} disabled={busy} onChange={(v) => { if (!busy) onSave(v); }} />
      <LiveLine liveValue={liveValue} value={effOr(value, liveValue)} />
    </FieldFrame>
  );
}

// === HTML field (textarea + live preview) ==================================

function HtmlField({ label, fieldKey, value, source, liveValue, onSave, onReset, busy, promote, staged }) {
  const [editing, setEditing] = useState(false);
  // No value in our tiers → display the platform's live content as the value.
  const effective = (!value && liveValue != null && liveValue !== '') ? liveValue : value;
  const [draft, setDraft] = useState(effective || '');
  const [view, setView] = useState('rendered'); // 'rendered' | 'code'
  const [showLive, setShowLive] = useState(false);
  useEffect(() => { if (!editing) setDraft(effective || ''); }, [effective, editing]);
  const overridden = source === 'event';
  const commit = async () => {
    if (busy) return;
    await onSave(draft);
    setEditing(false);
  };
  const liveDiffers = liveValue != null && liveValue !== '' && !liveEq(liveValue, effective ?? '');

  return (
    <div className="py-2 border-t border-border first:border-0 text-xs">
      <div className="flex items-center justify-between mb-1">
        <div>
          <span className="font-medium">{label || fieldKey}</span>
          <span className="text-[10px] text-muted-foreground font-mono ml-2">{fieldKey}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {!editing && (
            <div className="flex text-[10px] border border-input rounded overflow-hidden">
              <button onClick={() => setView('rendered')} className={`px-1.5 py-0.5 ${view === 'rendered' ? 'bg-accent' : 'hover:bg-accent/50'}`}>Rendered</button>
              <button onClick={() => setView('code')} className={`px-1.5 py-0.5 border-l border-input ${view === 'code' ? 'bg-accent' : 'hover:bg-accent/50'}`}>Code</button>
            </div>
          )}
          <StagedDot staged={staged} />
          <SourceBadge source={source} />
          {!editing && <PromoteButtons promote={promote} busy={busy} />}
          {!editing && overridden && onReset && <ResetButton onReset={onReset} busy={busy} />}
          {editing ? (
            <>
              <button onClick={commit} disabled={busy} aria-disabled={busy} className="text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded disabled:opacity-50">{busy ? 'saving…' : 'save'}</button>
              <button onClick={() => setEditing(false)} disabled={busy} className="text-[10px] px-1.5 py-0.5 border border-input rounded">cancel</button>
            </>
          ) : (
            <button onClick={() => { if (!busy) { setEditing(true); setView('code'); } }} disabled={busy} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-50">edit</button>
          )}
        </div>
      </div>
      {editing ? (
        <div className="grid grid-cols-2 gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={10}
            className="px-2 py-1 border border-input rounded-md bg-background font-mono text-[11px]"
            placeholder="<p>HTML here</p>"
          />
          <div
            className="prose prose-sm dark:prose-invert max-w-none border border-border rounded-md p-2 bg-muted/20 overflow-y-auto text-[11px]"
            style={{ maxHeight: '280px' }}
            dangerouslySetInnerHTML={{ __html: draft || '<em class="text-muted-foreground">preview…</em>' }}
          />
        </div>
      ) : !effective ? (
        <em className="text-muted-foreground">empty</em>
      ) : view === 'code' ? (
        <pre className="border border-border rounded-md p-2 bg-muted/30 text-[10px] font-mono whitespace-pre-wrap break-all max-h-40 overflow-y-auto">{effective}</pre>
      ) : (
        <div
          className="prose prose-sm dark:prose-invert max-w-none border border-border rounded-md p-2 bg-muted/20 text-[11px] max-h-40 overflow-y-auto"
          dangerouslySetInnerHTML={{ __html: effective }}
        />
      )}
      {!editing && liveDiffers && (
        <div className="text-[10px] text-sky-700 dark:text-sky-300 mt-1">
          <button onClick={() => setShowLive((v) => !v)} className="hover:underline">{showLive ? '▾' : '▸'} live on platform differs</button>
          {showLive && (
            <div className="prose prose-sm dark:prose-invert max-w-none border border-sky-500/30 rounded-md p-2 bg-sky-500/5 mt-1 max-h-40 overflow-y-auto text-foreground" dangerouslySetInnerHTML={{ __html: String(liveValue) }} />
          )}
        </div>
      )}
    </div>
  );
}

// === collection header (shared by ladder/host/discount/add-on/email) =======
function CollectionHeader({ title, count, source, editing, onAdd, onCancel, onSave, onEdit, onReset, busy, addLabel = '+ row' }) {
  const overridden = source === 'event';
  return (
    <div className="flex items-center justify-between mb-1">
      <div className="text-[10px] uppercase text-muted-foreground flex items-center gap-2">
        {title} ({count}){source && <SourceBadge source={source} />}
      </div>
      {editing ? (
        <div className="flex gap-1">
          <button onClick={onAdd} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">{addLabel}</button>
          <button onClick={onCancel} disabled={busy} className="text-[10px] px-1.5 py-0.5 border border-input rounded">cancel</button>
          <button onClick={onSave} disabled={busy} aria-disabled={busy} className="text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded disabled:opacity-50">{busy ? 'saving…' : 'save'}</button>
        </div>
      ) : (
        <div className="flex gap-1">
          {overridden && onReset && <ResetButton onReset={onReset} busy={busy} label="reset to template" />}
          <button onClick={() => { if (!busy) onEdit(); }} disabled={busy} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-50">edit</button>
        </div>
      )}
    </div>
  );
}

// === rate ladder ===========================================================

// Tiers are grouped into ladders (Female / Male / VIP …, derived from the
// name) and shown as cards. Position within a ladder IS the preemptive order,
// so dragging reorders the escalation and rewrites sort_index + preemptive_ref
// on save — `#`/`ref` never surface. Sold counts come from live (Phase J);
// a tier with sales can't be removed (Phase I rule).

let _tierUid = 0;

// Visibility = the configured on-sale setting (plain-worded). NOT sold-out.
function VisibilityChip({ state }) {
  const s = (state || 'ACTIVE').toUpperCase();
  const tone = s === 'ACTIVE' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
    : s === 'INACTIVE' ? 'bg-muted text-muted-foreground'
    : 'bg-amber-400/15 text-amber-700 dark:text-amber-400'; // hidden / locked / code-only
  const label = { ACTIVE: 'On sale', INACTIVE: 'Off', HIDDEN: 'Hidden', LOCKED: 'Code only' }[s] || s.toLowerCase();
  const title = { ACTIVE: 'Enabled and on sale', INACTIVE: 'Not on sale', HIDDEN: 'Not shown publicly', LOCKED: 'Requires an access code' }[s] || '';
  return <span className={`text-[10px] px-1.5 py-0.5 rounded ${tone}`} title={title}>{label}</span>;
}

// True current status from live data: whether the tier is actually showing to
// buyers right now — not just its configured state. "Selling now" = open;
// "Queued" = configured on sale but gated behind a tier that hasn't sold out.
const LIVE_STATUS = {
  selling:  ['Selling now', 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300', 'Open and visible to buyers now'],
  queued:   ['Tiered',      'bg-amber-400/15 text-amber-700 dark:text-amber-400', 'On sale, but not visible yet; opens when the tier it sits behind sells out'],
  sold_out: ['SOLD OUT',    'bg-red-500/15 text-red-700 dark:text-red-300 font-medium', 'Sold to capacity'],
  off:      ['Off',         'bg-muted text-muted-foreground', 'Not on sale'],
  hidden:   ['Hidden',      'bg-amber-400/15 text-amber-700 dark:text-amber-400', 'Not shown publicly'],
  locked:   ['Code only',   'bg-amber-400/15 text-amber-700 dark:text-amber-400', 'Requires an access code'],
};
function LiveStatusChip({ status, opensAfter }) {
  let [label, tone, title] = LIVE_STATUS[status] || ['—', 'bg-muted text-muted-foreground', ''];
  // The pill stays compact ("Tiered"); the tooltip names the actual gate.
  if (status === 'queued' && opensAfter) {
    title = `On sale, but not visible yet; opens when “${opensAfter}” sells out`;
  }
  return <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${tone}`} title={title}>{label}</span>;
}
function RemainingChip({ remaining, sold }) {
  if (remaining == null) return null;
  return <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-700 dark:text-sky-300 whitespace-nowrap" title={`${sold ?? 0} sold`}>{remaining} left</span>;
}

// sale_start/sale_end are stored as epoch seconds (multiverse start_stamp/end_stamp).
// Render them in a datetime-local input (browser-local tz) and convert back.
function stampToLocalInput(secs) {
  if (secs == null || secs === '') return '';
  const d = new Date(Number(secs) * 1000);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToStamp(v) {
  if (!v) return null;
  const ms = new Date(v).getTime();
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
}

function TierContent({ tier, gi, ti, editing, sold, setTier, removeTier, dragHandle, refName = {}, allTiers = [], onGate, chainTarget }) {
  const [adv, setAdv] = useState(false);
  // `sold` is the matched live rate (or undefined). When present it carries the
  // true current status (selling/queued/sold_out…) computed from the live
  // preemptive graph; otherwise we fall back to the configured visibility.
  const live = sold;
  const hasSales = live ? (live.hasSales || (live.sold ?? 0) > 0) : false;
  // Gate label from OUR preemptive_ref (resolved to a name), falling back to
  // the live gate when we haven't set one — so a drag/edit shows immediately.
  const gate = (tier.preemptive_ref && refName[tier.preemptive_ref]) || live?.opensAfter || null;
  const gateLine = gate && (
    <div className="text-[10px] text-muted-foreground truncate" title={`Opens when “${gate}” sells out`}>
      ↳ opens when <span className="text-foreground/70">{gate}</span> sells out
    </div>
  );
  // Two distinct signals, explicitly prefixed so "On sale" (a setting) is
  // never confused with "Selling now" (live reality).
  const statusChips = live ? (
    <span className="inline-flex items-center gap-1">
      <span className="text-[10px] text-muted-foreground">Now:</span>
      <LiveStatusChip status={live.liveStatus} opensAfter={live.opensAfter} />
      {live.liveStatus === 'selling' && <RemainingChip remaining={live.remaining} sold={live.sold} />}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1">
      <span className="text-[10px] text-muted-foreground">Setting:</span>
      <VisibilityChip state={tier.state} />
      <span className="text-[10px] text-muted-foreground" title="Not yet pushed to Universe">· not live</span>
    </span>
  );
  if (!editing) {
    return (
      <div className={`flex items-center gap-3 px-3 py-1.5 border-t border-border first:border-t-0 text-xs ${gate ? 'pl-7' : ''}`}>
        <div className="flex-1 min-w-0">
          <div className="truncate">{tier.name || <em className="text-muted-foreground">unnamed</em>}</div>
          {gateLine}
        </div>
        <div className="w-16 text-right tabular-nums font-medium">${tier.price ?? 0}</div>
        <div className="w-16 text-right text-muted-foreground">cap {tier.capacity ?? '—'}</div>
        {statusChips}
      </div>
    );
  }
  return (
    <div className={`border-t border-border first:border-t-0 ${chainTarget ? 'ring-2 ring-inset ring-sky-400/70 bg-sky-500/5' : ''}`}>
      {chainTarget && <div className="text-[10px] text-sky-600 dark:text-sky-300 px-3 pt-1">⇲ drop to open after “{tier.name}”</div>}
      <div className="flex items-center gap-2 px-2 py-1.5 text-xs">
        {dragHandle}
        <input value={tier.name || ''} onChange={(e) => setTier(gi, ti, 'name', e.target.value)} className="flex-1 min-w-0 px-1 py-0.5 border border-input rounded bg-background" placeholder="Tier name" />
        <span className="text-muted-foreground">$</span>
        <input type="number" value={tier.price ?? ''} onChange={(e) => setTier(gi, ti, 'price', e.target.value)} className="w-16 px-1 py-0.5 border border-input rounded bg-background text-right" />
        <input type="number" value={tier.capacity ?? ''} onChange={(e) => setTier(gi, ti, 'capacity', e.target.value)} className="w-14 px-1 py-0.5 border border-input rounded bg-background text-right" title="capacity" />
        <select value={tier.state || 'ACTIVE'} onChange={(e) => setTier(gi, ti, 'state', e.target.value)} className="px-1 py-0.5 border border-input rounded bg-background" title="visibility, whether the tier is on sale">
          <option value="ACTIVE">On sale</option>
          <option value="INACTIVE">Off</option>
          <option value="HIDDEN">Hidden</option>
          <option value="LOCKED">Code only</option>
        </select>
        {statusChips}
        <button onClick={() => setAdv((a) => !a)} className="text-[11px] px-1 py-0.5 border border-input rounded hover:bg-accent" title="advanced">⋯</button>
        <button
          onClick={() => { if (!hasSales) removeTier(gi, ti); }}
          disabled={hasSales}
          title={hasSales ? "Can't remove a tier that has sales" : 'remove tier'}
          className={`text-xs px-1 ${hasSales ? 'text-muted-foreground/40 cursor-not-allowed' : 'text-destructive hover:opacity-80'}`}
        >✕</button>
      </div>
      {gate && <div className="px-3 pb-1 pl-9">{gateLine}</div>}
      {adv && (
        <div className="px-3 pb-2 grid grid-cols-3 gap-2 text-[10px] text-muted-foreground">
          <label className="space-y-0.5">min per order<input type="number" value={tier.min_tickets ?? ''} onChange={(e) => setTier(gi, ti, 'min_tickets', e.target.value)} className="w-full px-1 py-0.5 border border-input rounded bg-background" /></label>
          <label className="space-y-0.5">max per order<input type="number" value={tier.max_tickets ?? ''} onChange={(e) => setTier(gi, ti, 'max_tickets', e.target.value)} className="w-full px-1 py-0.5 border border-input rounded bg-background" /></label>
          <label className="space-y-0.5">max redemptions<input type="number" value={tier.max_redemptions ?? ''} onChange={(e) => setTier(gi, ti, 'max_redemptions', e.target.value)} placeholder="1" className="w-full px-1 py-0.5 border border-input rounded bg-background" /></label>
          <div className="space-y-0.5">release offset<DurationField seconds={tier.release_offset ?? ''} onChange={(v) => setTier(gi, ti, 'release_offset', v ?? '')} /></div>
          <label className="space-y-0.5">client group<select value={tier.client_group || 'EVERYWHERE'} onChange={(e) => setTier(gi, ti, 'client_group', e.target.value)} className="w-full px-1 py-0.5 border border-input rounded bg-background"><option value="EVERYWHERE">Everywhere</option><option value="ONLINE_ONLY">Online only</option><option value="AT_THE_DOOR">At the door</option></select></label>
          {String(tier.state || '').toUpperCase() === 'LOCKED' && (
            <label className="space-y-0.5">access key<input value={tier.access_key ?? ''} onChange={(e) => setTier(gi, ti, 'access_key', e.target.value)} placeholder="Enter access key" className="w-full px-1 py-0.5 border border-input rounded bg-background" /></label>
          )}
          <label className="space-y-0.5">promotion label<input maxLength={15} value={tier.promotion_pill ?? ''} onChange={(e) => setTier(gi, ti, 'promotion_pill', e.target.value)} placeholder="e.g. Best Deal" className="w-full px-1 py-0.5 border border-input rounded bg-background" /></label>
          <label className="space-y-0.5">start ticket sale<input type="datetime-local" value={stampToLocalInput(tier.sale_start)} onChange={(e) => setTier(gi, ti, 'sale_start', localInputToStamp(e.target.value))} className="w-full px-1 py-0.5 border border-input rounded bg-background" /></label>
          <label className="space-y-0.5">end ticket sale<input type="datetime-local" value={stampToLocalInput(tier.sale_end)} onChange={(e) => setTier(gi, ti, 'sale_end', localInputToStamp(e.target.value))} className="w-full px-1 py-0.5 border border-input rounded bg-background" /></label>
          <label className="space-y-0.5 col-span-3">description<input value={tier.description ?? ''} onChange={(e) => setTier(gi, ti, 'description', e.target.value)} placeholder="e.g. ARRIVE by 11:30PM" className="w-full px-1 py-0.5 border border-input rounded bg-background" /></label>
          <label className="col-span-3 flex items-center gap-1.5"><input type="checkbox" checked={!!tier.redemption_message_active} onChange={(e) => setTier(gi, ti, 'redemption_message_active', e.target.checked)} /><span>display alert message when scanned</span></label>
          {tier.redemption_message_active && (
            <label className="space-y-0.5 col-span-3">alert message<textarea rows={2} value={tier.redemption_message ?? ''} onChange={(e) => setTier(gi, ti, 'redemption_message', e.target.value)} placeholder="e.g. CHARGE FOR LATE ARRIVAL" className="w-full px-1 py-0.5 border border-input rounded bg-background" /></label>
          )}
          <label className="space-y-0.5 col-span-3">opens after (or drag onto a tier)
            <select value={tier.preemptive_ref || ''} onChange={(e) => onGate && onGate(gi, ti, e.target.value || null)} className="w-full px-1 py-0.5 border border-input rounded bg-background">
              <option value="">· opens immediately</option>
              {allTiers.filter((o) => o.ref && o.ref !== tier.ref).map((o) => <option key={o.ref} value={o.ref}>{o.name}</option>)}
            </select>
          </label>
        </div>
      )}
    </div>
  );
}

// A tier row in edit mode: draggable (grip) AND a drop target. Dropping
// another tier onto it chains that tier behind this one.
function DraggableTierRow(props) {
  const { tier } = props;
  const { attributes, listeners, setNodeRef: dragRef, isDragging } = useDraggable({ id: `drag-${tier._id}` });
  const { setNodeRef: dropRef, isOver } = useDroppable({ id: `drop-${tier._id}` });
  const setRef = (n) => { dragRef(n); dropRef(n); };
  const handle = <span {...attributes} {...listeners} className="cursor-grab text-muted-foreground select-none px-0.5" title="drag onto another tier to chain it (opens after)">⠿</span>;
  return (
    <div ref={setRef} style={{ opacity: isDragging ? 0.4 : 1 }}>
      <TierContent {...props} dragHandle={handle} chainTarget={isOver} />
    </div>
  );
}

// Section header doubles as the "un-chain" drop zone (drop a tier here → opens
// immediately).
function LadderHeader({ gi, group, addLabel, onAdd, onAddBlank, dragging, onMove, canUp, canDown, groupHandle }) {
  const { setNodeRef, isOver } = useDroppable({ id: `unchain-${gi}` });
  return (
    <div ref={setNodeRef} className={`flex items-center justify-between px-3 py-1.5 bg-muted/40 border-b border-border ${isOver ? 'ring-2 ring-inset ring-sky-400/70' : ''}`}>
      <div className="text-xs font-medium flex items-center">
        {groupHandle}
        {group.label}
        <span className="text-muted-foreground font-normal"> · {group.tiers.length} tier{group.tiers.length === 1 ? '' : 's'}</span>
        {dragging && <span className="ml-2 text-[10px] text-sky-600 dark:text-sky-300">drop here to open immediately</span>}
      </div>
      <div className="flex items-center gap-1">
        {onMove && (
          <>
            <button onClick={() => canUp && onMove(-1)} disabled={!canUp}
              className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-30"
              title="Move this section up, changes the display order on the listing">↑</button>
            <button onClick={() => canDown && onMove(1)} disabled={!canDown}
              className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-30"
              title="Move this section down, changes the display order on the listing">↓</button>
          </>
        )}
        {onAdd && /\d/.test(addLabel || '') && <button onClick={onAdd} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">{addLabel}</button>}
        {onAddBlank && <button onClick={onAddBlank} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent" title="add a plain custom tier (no auto-numbering)">+ tier</button>}
      </div>
    </div>
  );
}

// Wraps a ladder card in the OUTER group-reorder DndContext: the header's
// ⠿ handle drags the whole section; dropping onto another card moves the
// section to that position. Hooks bind to the nearest provider ABOVE this
// component (the group context), so they don't collide with the per-card
// tier-chaining context nested inside.
function GroupShell({ gi, children, dragActive }) {
  const { attributes, listeners, setNodeRef: dragRef, isDragging } = useDraggable({ id: `gmove-${gi}` });
  const { setNodeRef: dropRef, isOver } = useDroppable({ id: `gslot-${gi}` });
  const handle = (
    <span ref={dragRef} {...attributes} {...listeners}
      className="cursor-grab text-muted-foreground select-none px-0.5 mr-1"
      title="drag to reorder this section (display order on the listing)">⠿</span>
  );
  return (
    <div ref={dropRef} style={{ opacity: isDragging ? 0.4 : 1 }}
      className={isOver ? 'ring-2 ring-sky-400/70 rounded-md' : dragActive ? 'ring-1 ring-sky-400/30 rounded-md' : ''}>
      {children(handle)}
    </div>
  );
}

function LadderCardEdit({ group, gi, sensors, soldByName, onDragEnd, setTier, removeTier, addTier, addBlankTier, refName, allTiers, onGate, onMoveGroup, groupCount, groupHandle }) {
  const [activeId, setActiveId] = useState(null);
  const tiers = group.tiers;
  const nextNum = nextTierTemplate(group).name.match(/tier\s*(\d+)/i);
  const addLabel = nextNum ? `+ Tier ${nextNum[1]}` : '+ tier';
  return (
    <div className="border border-border rounded-md overflow-hidden">
      <DndContext
        sensors={sensors}
        onDragStart={(e) => setActiveId(String(e.active.id).replace('drag-', ''))}
        onDragEnd={(e) => { setActiveId(null); onDragEnd(e); }}
        onDragCancel={() => setActiveId(null)}
      >
        <LadderHeader gi={gi} group={group} addLabel={addLabel} onAdd={() => addTier(gi)}
          onAddBlank={addBlankTier ? () => addBlankTier(gi) : null} dragging={!!activeId}
          onMove={onMoveGroup ? (dir) => onMoveGroup(gi, dir) : null} canUp={gi > 0} canDown={gi < (groupCount || 0) - 1}
          groupHandle={groupHandle} />
        <div>
          {tiers.map((t, ti) => (
            <DraggableTierRow
              key={t._id ?? ti} tier={t} gi={gi} ti={ti} editing
              sold={soldByName[t.name]} setTier={setTier} removeTier={removeTier}
              refName={refName} allTiers={allTiers} onGate={onGate}
            />
          ))}
        </div>
        <DragOverlay>
          {activeId ? (
            <div className="px-3 py-1.5 text-xs bg-background border border-sky-400 rounded shadow-lg">
              {tiers.find((t) => t._id === activeId)?.name || 'tier'}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

function LadderCardRead({ group, gi, soldByName, refName }) {
  return (
    <div className="border border-border rounded-md overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 bg-muted/40 border-b border-border">
        <div className="text-xs font-medium">
          {group.label}
          <span className="text-muted-foreground font-normal"> · {group.tiers.length} tier{group.tiers.length === 1 ? '' : 's'}</span>
        </div>
      </div>
      <div>
        {group.tiers.map((t, ti) => (
          <TierContent key={t._id ?? ti} tier={t} gi={gi} ti={ti} editing={false} sold={soldByName[t.name]} refName={refName} />
        ))}
      </div>
    </div>
  );
}

function LadderCard(props) {
  return props.editing ? <LadderCardEdit {...props} /> : <LadderCardRead {...props} />;
}

function RateLadderField({ value, source, liveRates, onSave, onReset, busy }) {
  const ourTiers = value || [];
  // When we have no captured ladder (orphan / venue default) but the event is
  // live on Universe, show the LIVE ladder as the baseline so ops sees reality.
  // Editing it persists these tiers as the event's override (captures live).
  const usingLive = source === 'default' && (liveRates?.length || 0) > 0;
  const tiers = usingLive ? liveRatesToTiers(liveRates) : ourTiers;
  const [editing, setEditing] = useState(false);
  const [groups, setGroups] = useState([]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const [activeGroupIdx, setActiveGroupIdx] = useState(null); // group being dragged (reorder overlay)

  // Give every tier a unique ref (so gate links resolve) + a drag id.
  const ensureRefsAndIds = (grps) => {
    const seen = new Set();
    let n = 0;
    return grps.map((g) => ({
      ...g,
      tiers: g.tiers.map((t) => {
        const slug = String(g.key || 't').toLowerCase().replace(/[^a-z0-9]+/g, '-');
        let ref = t.ref;
        if (!ref || seen.has(ref)) ref = `${slug}-${n}`;
        seen.add(ref);
        n += 1;
        return { ...t, ref, _id: t._id ?? `t${_tierUid++}` };
      }),
    }));
  };

  const startEdit = () => {
    setGroups(ensureRefsAndIds(groupIntoLadders(tiers)));
    setEditing(true);
  };
  const save = async () => {
    if (busy) return;
    const numOrNull = (v) => (v === '' || v == null ? null : Number(v));
    const strOrNull = (v) => { const s = (v ?? '').toString().trim(); return s || null; };
    const flat = applyOrder(groups).map((r) => {
      const price = Number(r.price) || 0;
      return {
        ...r,
        price,
        capacity: Number(r.capacity) || 0,
        // Type follows price: $0 → FREE_TICKET, anything above → PAID_TICKET.
        type: price > 0 ? 'PAID_TICKET' : 'FREE_TICKET',
        max_tickets: numOrNull(r.max_tickets),
        min_tickets: numOrNull(r.min_tickets),
        max_redemptions: numOrNull(r.max_redemptions),
        release_offset: Number(r.release_offset) || 0,
        promotion_pill: strOrNull(r.promotion_pill),
        description: (r.description ?? '').toString(),
        sale_start: numOrNull(r.sale_start),
        sale_end: numOrNull(r.sale_end),
        redemption_message_active: !!r.redemption_message_active,
        redemption_message: strOrNull(r.redemption_message),
        access_key: strOrNull(r.access_key),
      };
    });
    await onSave(flat);
    setEditing(false);
  };

  const soldByName = {};
  for (const r of liveRates || []) soldByName[r.name] = r;

  const setTier = (gi, ti, k, v) => setGroups((gs) => gs.map((g, i) => i !== gi ? g : { ...g, tiers: g.tiers.map((t, j) => j !== ti ? t : { ...t, [k]: v }) }));
  const removeTier = (gi, ti) => setGroups((gs) => gs.map((g, i) => i !== gi ? g : { ...g, tiers: g.tiers.filter((_, j) => j !== ti) }).filter((g) => g.tiers.length));
  // Set a tier's gate (preemptive_ref) — used by both the drag and the dropdown.
  const onGate = (gi, ti, ref) => setGroups((gs) => gs.map((g, i) => i !== gi ? g : { ...g, tiers: g.tiers.map((t, j) => j !== ti ? t : { ...t, preemptive_ref: ref || null }) }));
  // "+ Tier N": new tier defaults to chaining behind the last tier in the section.
  const addTier = (gi) => setGroups((gs) => gs.map((g, i) => {
    if (i !== gi) return g;
    const last = g.tiers[g.tiers.length - 1];
    const slug = String(g.key || 't').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const t = { ...nextTierTemplate(g), ref: `${slug}-new-${_tierUid}`, preemptive_ref: last?.ref || null, _id: `t${_tierUid++}` };
    return { ...g, tiers: [...g.tiers, t] };
  }));
  // "+ tier": a plain, custom tier — no "Tier N" numbering, not auto-chained.
  const addBlankTier = (gi) => setGroups((gs) => gs.map((g, i) => {
    if (i !== gi) return g;
    const slug = String(g.key || 't').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const t = { ...blankTierTemplate(g), ref: `${slug}-blank-${_tierUid}`, preemptive_ref: null, _id: `t${_tierUid++}` };
    return { ...g, tiers: [...g.tiers, t] };
  }));
  // Reorder whole sections (Female / Male / VIP …): swap in the groups array;
  // save re-sequences sort_index from display order, and the publisher's rate
  // sync detects the relative-order change and pushes the new sortIndex set.
  const moveGroup = (gi, dir) => setGroups((gs) => {
    const j = gi + dir;
    if (j < 0 || j >= gs.length) return gs;
    const next = [...gs];
    [next[gi], next[j]] = [next[j], next[gi]];
    return next;
  });
  const addLadder = () => setGroups((gs) => [...gs, {
    key: `Ladder ${gs.length + 1}`, label: `Ladder ${gs.length + 1}`,
    tiers: [{ name: 'New Tier', price: 0, capacity: 50, state: 'ACTIVE', client_group: 'EVERYWHERE', release_offset: 86400, ref: `ladder-${gs.length + 1}-0`, _id: `t${_tierUid++}` }],
  }]);
  // Drag end: drop onto a tier (`drop-<id>`) → chain behind it; onto the header
  // (`unchain-<gi>`) → opens immediately.
  const onDragEnd = (gi) => ({ active, over }) => {
    if (!over) return;
    const activeId = String(active.id).replace('drag-', '');
    const overId = String(over.id);
    setGroups((gs) => gs.map((g, i) => {
      if (i !== gi) return g;
      if (overId.startsWith('drop-')) {
        const targetId = overId.replace('drop-', '');
        if (targetId === activeId) return g;
        const targetRef = g.tiers.find((t) => t._id === targetId)?.ref || null;
        return { ...g, tiers: g.tiers.map((t) => t._id === activeId ? { ...t, preemptive_ref: targetRef } : t) };
      }
      if (overId.startsWith('unchain-')) {
        return { ...g, tiers: g.tiers.map((t) => t._id === activeId ? { ...t, preemptive_ref: null } : t) };
      }
      return g;
    }));
  };

  const displayGroups = editing ? groups : groupIntoLadders(tiers);
  // ref → name across all visible tiers, for resolving gate labels.
  const refName = {};
  for (const g of displayGroups) for (const t of g.tiers) if (t.ref) refName[t.ref] = t.name;
  const allTiers = displayGroups.flatMap((g) => g.tiers.map((t) => ({ ref: t.ref, name: t.name })));

  return (
    <div>
      <CollectionHeader
        title="Rate ladder" count={tiers.length} source={usingLive ? undefined : source}
        editing={editing} onAdd={addLadder} addLabel="+ ladder"
        onCancel={() => setEditing(false)} onSave={save} onEdit={startEdit} onReset={onReset} busy={busy}
      />
      {usingLive && !editing && (
        <div className="text-[10px] text-sky-700 dark:text-sky-300 bg-sky-500/10 border border-sky-500/20 rounded px-2 py-1 mb-2">
          Live Universe ladder, not customized for this event yet. Edit any tier to start an override.
        </div>
      )}
      {editing && (
        <div className="text-[10px] text-muted-foreground bg-muted/40 border border-border rounded px-2 py-1 mb-2">
          Tip: drag the ⠿ handle of one tier <span className="text-foreground/70">onto another tier</span> to make it open after that tier sells out · drag it <span className="text-foreground/70">onto the ladder header</span> to open it immediately.
        </div>
      )}
      <div className="space-y-2">
        {editing ? (
          <DndContext sensors={sensors}
            onDragStart={({ active }) => setActiveGroupIdx(Number(String(active.id).replace('gmove-', '')))}
            onDragCancel={() => setActiveGroupIdx(null)}
            onDragEnd={({ active, over }) => {
            setActiveGroupIdx(null);
            if (!over) return;
            const from = Number(String(active.id).replace('gmove-', ''));
            const to = Number(String(over.id).replace('gslot-', ''));
            if (Number.isNaN(from) || Number.isNaN(to) || from === to) return;
            setGroups((gs) => {
              const next = [...gs];
              const [moved] = next.splice(from, 1);
              next.splice(to, 0, moved);
              return next;
            });
          }}>
            <div className="space-y-2">
              {displayGroups.map((g, gi) => (
                <GroupShell key={g.key + gi} gi={gi} dragActive={activeGroupIdx != null && activeGroupIdx !== gi}>
                  {(handle) => (
                    <LadderCard
                      group={g} gi={gi} editing sensors={sensors}
                      soldByName={soldByName} onDragEnd={onDragEnd(gi)}
                      setTier={setTier} removeTier={removeTier} addTier={addTier} addBlankTier={addBlankTier}
                      refName={refName} allTiers={allTiers} onGate={onGate}
                      onMoveGroup={moveGroup} groupCount={displayGroups.length}
                      groupHandle={handle}
                    />
                  )}
                </GroupShell>
              ))}
            </div>
            <DragOverlay>
              {activeGroupIdx != null ? (
                <div className="px-3 py-1.5 text-xs bg-background border border-sky-400 rounded shadow-lg">
                  {displayGroups[activeGroupIdx]?.label} · {displayGroups[activeGroupIdx]?.tiers.length} tier{displayGroups[activeGroupIdx]?.tiers.length === 1 ? '' : 's'}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : displayGroups.map((g, gi) => (
          <LadderCard
            key={g.key + gi} group={g} gi={gi} editing={editing} sensors={sensors}
            soldByName={soldByName} onDragEnd={onDragEnd(gi)}
            setTier={setTier} removeTier={removeTier} addTier={addTier}
            refName={refName} allTiers={allTiers} onGate={onGate}
          />
        ))}
        {!displayGroups.length && (
          <div className="text-xs text-muted-foreground italic border border-dashed border-border rounded p-3">
            No tiers. {editing ? 'Use “+ ladder” to add one.' : ''}
          </div>
        )}
      </div>
    </div>
  );
}

// === host fields ===========================================================

function HostFieldsField({ value, source, liveHostFields, onSave, onReset, busy }) {
  const ours = value || [];
  // No captured host fields but live has them → show live as the baseline
  // (first edit captures them as an override), same pattern as the ladder.
  const usingLive = ours.length === 0 && (liveHostFields?.length || 0) > 0;
  const fields = usingLive ? liveHostFields : ours;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(fields);
  useEffect(() => { if (!editing) setDraft(fields.map((h) => ({ ...h }))); }, [value, editing, usingLive]); // eslint-disable-line

  const setRow = (i, k, v) => setDraft((d) => d.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const remove = (i) => setDraft((d) => d.filter((_, j) => j !== i));
  const add = () => setDraft((d) => [...d, {
    name: '', type: 'Text', subtype: null, context: 'Ticket',
    required: false, included: true, editable: true, multiple: false,
    sort_index: d.length, has_other: false, text: '', select_options: null,
  }]);
  const save = async () => {
    if (busy) return;
    await onSave(draft.map((h, i) => ({ ...h, sort_index: i })));
    setEditing(false);
  };

  return (
    <div>
      <CollectionHeader
        title="Host fields" count={(editing ? draft : fields).length} source={usingLive ? undefined : source}
        editing={editing} onAdd={add} onCancel={() => { setEditing(false); setDraft(fields); }}
        onSave={save} onEdit={() => setEditing(true)} onReset={onReset} busy={busy}
      />
      {usingLive && !editing && (
        <div className="text-[10px] text-sky-700 dark:text-sky-300 bg-sky-500/10 border border-sky-500/20 rounded px-2 py-1 mb-2">
          Live Universe host fields, not customized for this event yet.
        </div>
      )}
      <div className="border border-border rounded overflow-hidden overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-muted/40 text-left text-muted-foreground">
            <tr>
              <th className="px-2 py-1">name</th><th className="px-2 py-1">type</th><th className="px-2 py-1">subtype</th>
              <th className="px-2 py-1">context</th><th className="px-2 py-1">required</th><th className="px-2 py-1">included</th>
              {editing && <th className="px-2 py-1"></th>}
            </tr>
          </thead>
          <tbody>
            {(editing ? draft : fields).map((h, i) => (
              <tr key={i} className="border-t border-border">
                {editing ? (
                  <>
                    <td className="px-1 py-1"><input value={h.name || ''} onChange={(e) => setRow(i, 'name', e.target.value)} className="w-full px-1 border border-input rounded bg-background" /></td>
                    <td className="px-1 py-1">
                      <select value={h.type || 'Text'} onChange={(e) => setRow(i, 'type', e.target.value)} className="px-1 border border-input rounded bg-background">
                        <option>Text</option><option>Checkbox</option><option>Select</option><option>Number</option>
                      </select>
                    </td>
                    <td className="px-1 py-1"><input value={h.subtype || ''} onChange={(e) => setRow(i, 'subtype', e.target.value || null)} className="w-20 px-1 border border-input rounded bg-background" /></td>
                    <td className="px-1 py-1">
                      <select value={h.context || 'Ticket'} onChange={(e) => setRow(i, 'context', e.target.value)} className="px-1 border border-input rounded bg-background">
                        <option>Ticket</option><option>CostItem</option>
                      </select>
                    </td>
                    <td className="px-1 py-1 text-center"><input type="checkbox" checked={!!h.required} onChange={(e) => setRow(i, 'required', e.target.checked)} /></td>
                    <td className="px-1 py-1 text-center"><input type="checkbox" checked={!!h.included} onChange={(e) => setRow(i, 'included', e.target.checked)} /></td>
                    <td className="px-1 py-1 text-right"><button onClick={() => remove(i)} className="text-destructive">×</button></td>
                  </>
                ) : (
                  <>
                    <td className="px-2 py-1">{h.name}</td>
                    <td className="px-2 py-1 text-muted-foreground">{h.type}</td>
                    <td className="px-2 py-1 text-muted-foreground">{h.subtype || '—'}</td>
                    <td className="px-2 py-1 text-muted-foreground">{h.context || '—'}</td>
                    <td className="px-2 py-1 text-center">{h.required ? '✓' : '—'}</td>
                    <td className="px-2 py-1 text-center">{h.included ? '✓' : '—'}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// === discount codes ========================================================

function DiscountsField({ value, source, onSave, onReset, busy }) {
  const codes = value || [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(codes);
  const [err, setErr] = useState(null);
  useEffect(() => { if (!editing) setDraft(codes); }, [value, editing]); // eslint-disable-line

  const setRow = (i, k, v) => setDraft((d) => d.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const remove = (i) => setDraft((d) => d.filter((_, j) => j !== i));
  const add = () => setDraft((d) => [...d, { code: '', percent: null, fixed: null, quantity: null, state: 'ACTIVE', redemptionType: 'CODE' }]);
  const save = async () => {
    if (busy) return;
    setErr(null);
    try {
      const clean = draft.filter((r) => r.code && r.code.trim()).map((r) => ({
        code: r.code.trim().toUpperCase(),
        ...(r.percent != null && r.percent !== '' ? { percent: Number(r.percent) } : {}),
        ...(r.fixed != null && r.fixed !== '' ? { fixed: Number(r.fixed) } : {}),
        ...(r.quantity != null && r.quantity !== '' ? { quantity: Number(r.quantity) } : {}),
        state: r.state || 'ACTIVE', redemptionType: r.redemptionType || 'CODE',
      }));
      await onSave(clean.length ? clean : null);
      setEditing(false);
    } catch (e) { setErr(e.message); }
  };

  return (
    <div>
      <CollectionHeader
        title="Discount codes" count={(editing ? draft : codes).length} source={source}
        editing={editing} onAdd={add} onCancel={() => { setEditing(false); setDraft(codes); }}
        onSave={save} onEdit={() => setEditing(true)} onReset={onReset} busy={busy} addLabel="+ code"
      />
      {err && <div className="text-xs text-destructive mb-1">{err}</div>}
      {(editing ? draft : codes).length === 0 && !editing ? (
        <div className="text-xs text-muted-foreground italic">No discount codes. Universe also auto-tracks any code on usage even when not pre-registered.</div>
      ) : (
        <div className="border border-border rounded overflow-hidden overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-muted/40 text-left text-muted-foreground">
              <tr>
                <th className="px-2 py-1">code</th><th className="px-2 py-1 text-right">% off</th><th className="px-2 py-1 text-right">$ off</th>
                <th className="px-2 py-1 text-right">quantity</th><th className="px-2 py-1">state</th><th className="px-2 py-1">type</th>{editing && <th className="px-2 py-1"></th>}
              </tr>
            </thead>
            <tbody>
              {(editing ? draft : codes).map((r, i) => (
                <tr key={i} className="border-t border-border">
                  {editing ? (
                    <>
                      <td className="px-1 py-1"><input value={r.code || ''} onChange={(e) => setRow(i, 'code', e.target.value)} className="w-full px-1 border border-input rounded bg-background font-mono uppercase" /></td>
                      <td className="px-1 py-1"><input type="number" min="0" max="100" value={r.percent ?? ''} onChange={(e) => setRow(i, 'percent', e.target.value)} className="w-16 px-1 border border-input rounded bg-background text-right" /></td>
                      <td className="px-1 py-1"><input type="number" min="0" value={r.fixed ?? ''} onChange={(e) => setRow(i, 'fixed', e.target.value)} className="w-16 px-1 border border-input rounded bg-background text-right" /></td>
                      <td className="px-1 py-1"><input type="number" min="0" value={r.quantity ?? ''} onChange={(e) => setRow(i, 'quantity', e.target.value)} className="w-20 px-1 border border-input rounded bg-background text-right" placeholder="Unlimited" /></td>
                      <td className="px-1 py-1"><select value={r.state || 'ACTIVE'} onChange={(e) => setRow(i, 'state', e.target.value)} className="px-1 border border-input rounded bg-background"><option value="ACTIVE">On sale</option><option value="INACTIVE">Off</option></select></td>
                      <td className="px-1 py-1"><select value={r.redemptionType || 'CODE'} onChange={(e) => setRow(i, 'redemptionType', e.target.value)} className="px-1 border border-input rounded bg-background"><option value="CODE">Code</option><option value="LINK">Link</option></select></td>
                      <td className="px-1 py-1 text-right"><button onClick={() => remove(i)} className="text-destructive">×</button></td>
                    </>
                  ) : (
                    <>
                      <td className="px-2 py-1 font-mono">{r.code}</td>
                      <td className="px-2 py-1 text-right">{r.percent != null ? `${r.percent}%` : '—'}</td>
                      <td className="px-2 py-1 text-right">{r.fixed != null ? `$${r.fixed}` : '—'}</td>
                      <td className="px-2 py-1 text-right">{r.quantity ?? 'Unlimited'}</td>
                      <td className="px-2 py-1 text-muted-foreground">{(r.state || 'ACTIVE') === 'ACTIVE' ? 'On sale' : 'Off'}</td>
                      <td className="px-2 py-1 text-muted-foreground">{r.redemptionType === 'LINK' ? 'Link' : 'Code'}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// === add-ons ===============================================================

function AddOnsField({ value, source, onSave, onReset, busy }) {
  const addOns = value || [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(addOns);
  const [err, setErr] = useState(null);
  useEffect(() => { if (!editing) setDraft(addOns); }, [value, editing]); // eslint-disable-line

  const setRow = (i, k, v) => setDraft((d) => d.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const remove = (i) => setDraft((d) => d.filter((_, j) => j !== i));
  const add = () => setDraft((d) => [...d, { name: '', description: '', price: 0, capacity: null, currency: 'USD', hostPaysCommission: true, suppressQrCode: false }]);
  const save = async () => {
    if (busy) return;
    setErr(null);
    try {
      const clean = draft.filter((r) => r.name && r.name.trim()).map((r) => ({
        name: r.name.trim(),
        ...(r.description ? { description: r.description } : {}),
        currency: r.currency || 'USD', price: Number(r.price) || 0,
        ...(r.capacity != null && r.capacity !== '' ? { capacity: Number(r.capacity) } : {}),
        hostPaysCommission: !!r.hostPaysCommission, suppressQrCode: !!r.suppressQrCode,
        ...(r.postPurchaseMessage ? { postPurchaseMessage: r.postPurchaseMessage } : {}),
      }));
      await onSave(clean.length ? clean : null);
      setEditing(false);
    } catch (e) { setErr(e.message); }
  };

  return (
    <div>
      <CollectionHeader
        title="Add-ons" count={(editing ? draft : addOns).length} source={source}
        editing={editing} onAdd={add} onCancel={() => { setEditing(false); setDraft(addOns); }}
        onSave={save} onEdit={() => setEditing(true)} onReset={onReset} busy={busy} addLabel="+ add-on"
      />
      {err && <div className="text-xs text-destructive mb-1">{err}</div>}
      {(editing ? draft : addOns).length === 0 && !editing ? (
        <div className="text-xs text-muted-foreground italic">No add-ons. Add per-event items like merch, photo packages, or table service upgrades.</div>
      ) : (
        <div className="space-y-2">
          {(editing ? draft : addOns).map((a, i) => (
            <div key={i} className="border border-border rounded p-2 text-[11px]">
              {editing ? (
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-3"><label className="block text-muted-foreground mb-0.5">name</label><input value={a.name || ''} onChange={(e) => setRow(i, 'name', e.target.value)} className="w-full px-1 border border-input rounded bg-background" /></div>
                  <div><label className="block text-muted-foreground mb-0.5">price</label><input type="number" step="0.01" value={a.price ?? 0} onChange={(e) => setRow(i, 'price', e.target.value)} className="w-full px-1 border border-input rounded bg-background text-right" /></div>
                  <div><label className="block text-muted-foreground mb-0.5">capacity</label><input type="number" value={a.capacity ?? ''} placeholder="Unlimited" onChange={(e) => setRow(i, 'capacity', e.target.value)} className="w-full px-1 border border-input rounded bg-background text-right" /></div>
                  <div><label className="block text-muted-foreground mb-0.5">currency</label><input value={a.currency || 'USD'} onChange={(e) => setRow(i, 'currency', e.target.value.toUpperCase())} className="w-full px-1 border border-input rounded bg-background font-mono" /></div>
                  <div className="col-span-3"><label className="block text-muted-foreground mb-0.5">description</label><textarea value={a.description || ''} onChange={(e) => setRow(i, 'description', e.target.value)} rows={2} className="w-full px-1 border border-input rounded bg-background" /></div>
                  <div className="col-span-3 flex gap-4">
                    <label className="flex items-center gap-1"><input type="checkbox" checked={!!a.hostPaysCommission} onChange={(e) => setRow(i, 'hostPaysCommission', e.target.checked)} /> host pays commission</label>
                    <label className="flex items-center gap-1"><input type="checkbox" checked={!!a.suppressQrCode} onChange={(e) => setRow(i, 'suppressQrCode', e.target.checked)} /> suppress QR code</label>
                  </div>
                  <div className="col-span-3 text-right"><button onClick={() => remove(i)} className="text-destructive text-[10px]">remove add-on</button></div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{a.name}</div>
                    <div className="text-muted-foreground">${a.price ?? 0} {a.currency || 'USD'}{a.capacity ? ` · cap ${a.capacity}` : ''}</div>
                  </div>
                  {a.description && <div className="text-muted-foreground mt-1">{a.description}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// === email templates =======================================================

const COLOR_SCHEMES = ['DEFAULT', 'PURPLE', 'GREEN', 'BLUE', 'RED', 'GREY', 'BLACK'];
const COLOR_HEX = { DEFAULT: '#6366f1', PURPLE: '#7c3aed', GREEN: '#16a34a', BLUE: '#2563eb', RED: '#dc2626', GREY: '#6b7280', BLACK: '#111827' };
const TEMPLATE_TYPES = ['confirmation', 'reminder', 'cancellation', 'reschedule'];

function ColorSwatch({ scheme }) {
  const hex = COLOR_HEX[(scheme || 'DEFAULT').toUpperCase()] || COLOR_HEX.DEFAULT;
  return <span className="inline-block w-3 h-3 rounded-sm border border-border align-middle mr-1" style={{ background: hex }} />;
}

function EmailTemplatesField({ value, source, onSave, onReset, busy }) {
  const templates = value || [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(templates);
  const [err, setErr] = useState(null);
  useEffect(() => { if (!editing) setDraft(templates); }, [value, editing]); // eslint-disable-line

  const setRow = (i, k, v) => setDraft((d) => d.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const remove = (i) => setDraft((d) => d.filter((_, j) => j !== i));
  const add = () => setDraft((d) => [...d, { type: 'confirmation', subject: '', colorScheme: 'DEFAULT', logoUrl: '', headerContent: '', footerContent: '' }]);
  const save = async () => {
    if (busy) return;
    setErr(null);
    try {
      const clean = draft.filter((t) => t.type).map((t) => ({
        type: t.type,
        ...(t.subject ? { subject: t.subject } : {}),
        ...(t.colorScheme ? { colorScheme: t.colorScheme } : {}),
        ...(t.logoUrl ? { logoUrl: t.logoUrl } : {}),
        ...(t.headerContent ? { headerContent: t.headerContent } : {}),
        ...(t.footerContent ? { footerContent: t.footerContent } : {}),
        ...(t.accessibilityContent ? { accessibilityContent: t.accessibilityContent } : {}),
        ...(t.additionalInfo ? { additionalInfo: t.additionalInfo } : {}),
      }));
      await onSave(clean.length ? clean : null);
      setEditing(false);
    } catch (e) { setErr(e.message); }
  };

  return (
    <div>
      <CollectionHeader
        title="Email templates" count={(editing ? draft : templates).length} source={source}
        editing={editing} onAdd={add} onCancel={() => { setEditing(false); setDraft(templates); }}
        onSave={save} onEdit={() => setEditing(true)} onReset={onReset} busy={busy} addLabel="+ template"
      />
      {err && <div className="text-xs text-destructive mb-1">{err}</div>}
      {(editing ? draft : templates).length === 0 && !editing && (
        <div className="text-xs text-muted-foreground italic">No custom email templates. Universe sends its default confirmation/reminder emails unless overridden here.</div>
      )}
      <div className="space-y-2">
        {(editing ? draft : templates).map((t, i) => (
          <div key={i} className="border border-border rounded p-3 text-[11px]">
            {editing ? (
              <div className="grid grid-cols-3 gap-2">
                <div><label className="block text-muted-foreground mb-0.5">type</label><select value={t.type || 'confirmation'} onChange={(e) => setRow(i, 'type', e.target.value)} className="w-full px-1 border border-input rounded bg-background">{TEMPLATE_TYPES.map((x) => <option key={x}>{x}</option>)}</select></div>
                <div className="col-span-2"><label className="block text-muted-foreground mb-0.5">subject</label><input value={t.subject || ''} onChange={(e) => setRow(i, 'subject', e.target.value)} className="w-full px-1 border border-input rounded bg-background" placeholder="Your tickets to {event.title}" /></div>
                <div><label className="block text-muted-foreground mb-0.5">color scheme</label><select value={t.colorScheme || 'DEFAULT'} onChange={(e) => setRow(i, 'colorScheme', e.target.value)} className="w-full px-1 border border-input rounded bg-background">{COLOR_SCHEMES.map((x) => <option key={x}>{x}</option>)}</select></div>
                <div className="col-span-2"><label className="block text-muted-foreground mb-0.5">logo URL</label><input value={t.logoUrl || ''} onChange={(e) => setRow(i, 'logoUrl', e.target.value)} className="w-full px-1 border border-input rounded bg-background font-mono text-[10px]" /></div>
                <div className="col-span-3"><label className="block text-muted-foreground mb-0.5">header content (HTML)</label><textarea value={t.headerContent || ''} onChange={(e) => setRow(i, 'headerContent', e.target.value)} rows={3} className="w-full px-1 border border-input rounded bg-background font-mono text-[10px]" /></div>
                <div className="col-span-3"><label className="block text-muted-foreground mb-0.5">footer content (HTML)</label><textarea value={t.footerContent || ''} onChange={(e) => setRow(i, 'footerContent', e.target.value)} rows={3} className="w-full px-1 border border-input rounded bg-background font-mono text-[10px]" /></div>
                <div className="col-span-3"><label className="block text-muted-foreground mb-0.5">accessibility content (HTML)</label><textarea value={t.accessibilityContent || ''} onChange={(e) => setRow(i, 'accessibilityContent', e.target.value)} rows={2} className="w-full px-1 border border-input rounded bg-background font-mono text-[10px]" /></div>
                <div className="col-span-3"><label className="block text-muted-foreground mb-0.5">additional info (HTML)</label><textarea value={t.additionalInfo || ''} onChange={(e) => setRow(i, 'additionalInfo', e.target.value)} rows={2} className="w-full px-1 border border-input rounded bg-background font-mono text-[10px]" /></div>
                <div className="col-span-3 text-right"><button onClick={() => remove(i)} className="text-destructive text-[10px]">remove template</button></div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                <div><span className="text-muted-foreground">type:</span> <code>{t.type}</code></div>
                <div className="col-span-2"><span className="text-muted-foreground">subject:</span> {t.subject || <em className="text-muted-foreground">default</em>}</div>
                <div><span className="text-muted-foreground">color:</span> <ColorSwatch scheme={t.colorScheme} />{(t.colorScheme || 'DEFAULT').charAt(0) + (t.colorScheme || 'DEFAULT').slice(1).toLowerCase()}</div>
                {t.logoUrl && <div className="col-span-2"><span className="text-muted-foreground">logo:</span> <code className="text-[10px]">{t.logoUrl.slice(0, 60)}…</code></div>}
                {t.headerContent && <div className="col-span-3"><span className="text-muted-foreground">header:</span> <span className="text-muted-foreground">{t.headerContent.length} chars</span></div>}
                {t.footerContent && <div className="col-span-3"><span className="text-muted-foreground">footer:</span> <span className="text-muted-foreground">{t.footerContent.length} chars</span></div>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// === images ================================================================

function ImageSlot({ label, current, source, aspect = 'aspect-square', onSave, onReset, busy }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState(null);
  const upload = async (file) => {
    if (!file || busy) return;
    setUploading(true); setErr(null);
    try { const uuid = await uploadcareUpload(file); await onSave({ uploadId: uuid }); }
    catch (e) { setErr(e.message); }
    finally { setUploading(false); }
  };
  const id = current?.uploadId;
  return (
    <div className="border border-border rounded-md p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium">{label}</div>
        <div className="flex items-center gap-1">
          <SourceBadge source={source} />
          {source === 'event' && onReset && <ResetButton onReset={onReset} busy={busy} />}
        </div>
      </div>
      {id ? (
        <img src={`https://ucarecdn.com/${id}/-/preview/400x400/`} alt={label} className={`w-full ${aspect} object-cover rounded`} />
      ) : (
        <div className={`w-full ${aspect} rounded bg-muted flex items-center justify-center text-xs text-muted-foreground`}>no image</div>
      )}
      <label className="text-[10px] text-primary cursor-pointer hover:underline">
        {uploading ? 'uploading…' : id ? 'replace for this event' : 'upload'}
        <input type="file" accept="image/jpeg,image/png,image/webp" disabled={uploading || busy} className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
      </label>
      {err && <div className="text-[10px] text-destructive">{err}</div>}
    </div>
  );
}

// Combined image editor — eventPhoto + coverPhoto + additionalPhotos[].
// Reads the three values out of the resolved payload; writes each via patch().
function ImagesField({ resolved, sources, live, patch, busy }) {
  const upd = resolved?.payload?.updateAttrs || {};
  // Fall back to the live Universe images when we haven't captured our own.
  const liveImg = live?.images || {};
  const eventPhoto = upd.eventPhoto || (liveImg.eventPhoto ? { uploadId: liveImg.eventPhoto } : undefined);
  const coverPhoto = upd.coverPhoto || (liveImg.coverPhoto ? { uploadId: liveImg.coverPhoto } : undefined);
  const additional = (upd.additionalPhotos && upd.additionalPhotos.length)
    ? upd.additionalPhotos
    : (liveImg.additionalPhotos || []).map((id) => ({ uploadId: id }));
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState(null);
  const addAdditional = async (files) => {
    if (!files?.length || busy) return;
    setUploading(true); setErr(null);
    try {
      const next = (additional || []).map((p) => p.uploadId);
      for (const f of Array.from(files)) next.push(await uploadcareUpload(f));
      await patch('additionalPhotos', next.map((id) => ({ uploadId: id })));
    } catch (e) { setErr(e.message); }
    finally { setUploading(false); }
  };
  const removeAdditional = (i) => patch('additionalPhotos', (additional || []).filter((_, j) => j !== i));

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <ImageSlot label="Flyer (square)" current={eventPhoto} source={sources.eventPhoto} aspect="aspect-square"
          onSave={(v) => patch('eventPhoto', v)} onReset={() => patch('eventPhoto', null)} busy={busy} />
        <ImageSlot label="Cover (banner 16:9)" current={coverPhoto} source={sources.coverPhoto} aspect="aspect-[16/9]"
          onSave={(v) => patch('coverPhoto', v)} onReset={() => patch('coverPhoto', null)} busy={busy} />
      </div>
      <div className="border border-border rounded-md p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-medium">Additional photos ({additional.length})</div>
          <div className="flex items-center gap-1">
            <SourceBadge source={sources.additionalPhotos} />
            {sources.additionalPhotos === 'event' && <ResetButton onReset={() => patch('additionalPhotos', null)} busy={busy} />}
            <label className="text-[10px] px-1.5 py-0.5 border border-input rounded text-primary cursor-pointer hover:bg-accent">
              {uploading ? 'uploading…' : '+ upload'}
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple disabled={uploading || busy} className="hidden"
                onChange={(e) => { addAdditional(e.target.files); e.target.value = ''; }} />
            </label>
          </div>
        </div>
        {err && <div className="text-[10px] text-destructive mb-2">{err}</div>}
        {additional.length === 0 ? (
          <div className="text-xs text-muted-foreground italic">No additional photos.</div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {additional.map((p, i) => (
              <div key={p.uploadId + i} className="relative group">
                <img src={`https://ucarecdn.com/${p.uploadId}/-/preview/200x200/`} alt="" className="w-full aspect-square object-cover rounded border border-border" />
                <button onClick={() => removeAdditional(i)} className="absolute top-0.5 right-0.5 text-[10px] px-1 bg-background/90 border border-border rounded text-destructive hover:bg-destructive hover:text-destructive-foreground opacity-0 group-hover:opacity-100">×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// === attractions (Ticketmaster discovery — platform-scoped search) =========

function AttractionsField({ resolved, event, ctx, patch, busy }) {
  const attractions = resolved?.payload?.updateAttrs?.attractions || event?.universe_overrides?.attractions || [];
  const individual = resolved?.payload?.updateAttrs?.individualAttractions || event?.universe_overrides?.individualAttractions || [];
  const source = resolved?.sources?.attractions || 'default';
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState(null);

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true); setSearchErr(null);
    try {
      const r = await apiFetch(`/api/tenants/${ctx.tenantSlug}/${ctx.platform}/attractions/search?q=${encodeURIComponent(q.trim())}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || 'search_failed');
      setResults(d.nodes || []);
    } catch (e) { setSearchErr(e.message); }
    finally { setSearching(false); }
  };
  // Sequential awaits: each patch saves + pushes + rebaselines; firing both
  // concurrently interleaves the pushes and the sync-row rebaselines.
  const attach = async (node, starred = false) => {
    await patch('attractions', [...attractions, { discoveryId: node.discoveryId }]);
    // `name` is display-only local data — the server strips it before the
    // Universe push (the eventUpdate input only takes discoveryId/starred/position/bio).
    await patch('individualAttractions', [...individual, { discoveryId: node.discoveryId, name: node.name || '', starred, position: individual.length, bio: node.bio || '' }]);
  };
  const detach = async (i) => {
    const removed = individual[i];
    await patch('individualAttractions', individual.filter((_, j) => j !== i));
    await patch('attractions', attractions.filter((a) => a.discoveryId !== removed?.discoveryId));
  };
  const star = (i) => patch('individualAttractions', individual.map((a, j) => ({ ...a, starred: j === i })));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase text-muted-foreground flex items-center gap-2">Attractions ({individual.length})<SourceBadge source={source} /></div>
        {source === 'event' && <ResetButton onReset={() => { patch('attractions', null); patch('individualAttractions', null); }} busy={busy} label="reset to template" />}
      </div>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="block text-xs text-muted-foreground mb-1">Search Ticketmaster artists</label>
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()} placeholder="e.g. Jason Derulo" className="w-full px-2 py-1 text-xs border border-input rounded-md bg-background" />
        </div>
        {event?.talent && q !== event.talent && (
          <button onClick={() => setQ(event.talent)} className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent">use talent: {event.talent}</button>
        )}
        <button onClick={search} disabled={searching || !q.trim()} className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50">{searching ? 'Searching…' : 'Search'}</button>
      </div>
      {searchErr && <div className="text-xs text-destructive">{searchErr}</div>}
      {results && (
        <div className="border border-border rounded-md max-h-48 overflow-y-auto">
          {results.length === 0 && <div className="text-xs text-muted-foreground p-2">No results.</div>}
          {results.map((node) => {
            const already = individual.some((a) => a.discoveryId === node.discoveryId);
            return (
              <div key={node.discoveryId} className="flex items-center justify-between px-2 py-1.5 border-t border-border first:border-0 text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  {node.ticketmasterImageUrl && <img src={node.ticketmasterImageUrl} alt="" className="w-8 h-8 rounded object-cover" />}
                  <div className="min-w-0"><div className="font-medium truncate">{node.name}</div><div className="text-muted-foreground text-[10px] font-mono truncate">{node.discoveryId}</div></div>
                </div>
                {already ? <span className="text-[10px] text-muted-foreground">attached</span> : (
                  <div className="flex gap-1">
                    <button onClick={() => attach(node, individual.length === 0)} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">attach</button>
                    <button onClick={() => attach(node, true)} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent" title="Attach as headliner (starred)">★ headliner</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase text-muted-foreground mb-1">Attached ({individual.length})</div>
        {individual.length === 0 ? (
          <div className="text-xs text-muted-foreground italic">No attractions attached. Search above to add the headliner and supporting acts.</div>
        ) : (
          <div className="space-y-1">
            {individual.map((a, i) => (
              <div key={a.discoveryId || i} className="flex items-center justify-between border border-border rounded p-2 text-xs">
                <div>
                  <div className="font-medium">{a.starred && <span className="text-amber-500 mr-1">★</span>}{a.name || a.discoveryId}</div>
                  {a.bio && <div className="text-muted-foreground text-[10px] mt-0.5">{a.bio.slice(0, 120)}{a.bio.length > 120 ? '…' : ''}</div>}
                </div>
                <div className="flex gap-1">
                  {!a.starred && <button onClick={() => star(i)} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">make headliner</button>}
                  <button onClick={() => detach(i)} className="text-[10px] px-1.5 py-0.5 border border-input rounded text-destructive hover:bg-destructive hover:text-destructive-foreground">remove</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// === venue & commerce (per-event overrides of Universe venue defaults) ======
// Universe stores all of this at the LISTING level; our venue config is just a
// defaults layer. These widgets let ops override per-event. Lifted from the
// venue editors in TenantSettings.jsx, adapted to the per-event contract.

// Category picker — fetches Universe's category list (cached server-side).
function CategoryField({ label, fieldKey, value, source, ctx, onSave, onReset, busy, optionsPath = 'categories', liveValue }) {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // No value in our tiers → the platform's live value IS the value.
  const effective = (value === undefined || value === null || value === '') ? liveValue : value;
  // Load on mount (not just when editing) so the read view shows the option
  // NAME, not the raw id. Server caches the list — one cheap call.
  useEffect(() => {
    if (list === null) {
      apiFetch(`/api/tenants/${ctx.tenantSlug}/${ctx.platform}/${optionsPath}`)
        .then((r) => r.json()).then((d) => setList((d.categories || d.templates || []).map((t) => ({ id: String(t.id), name: t.name })))).catch(() => setList([]));
    }
  }, [list, ctx.tenantSlug, ctx.platform, optionsPath]);
  const start = () => { setDraft(effective || ''); setEditing(true); };
  const commit = () => { if (busy) return; onSave(draft || null); setEditing(false); };
  const name = list?.find((c) => String(c.id) === String(effective))?.name;
  return (
    <FieldFrame label={label} fieldKey={fieldKey} source={source} onReset={onReset} busy={busy}
      editing={editing} onEdit={start} onCancel={() => setEditing(false)} onCommit={commit}>
      {editing ? (
        list ? (
          <select value={draft} onChange={(e) => setDraft(e.target.value)} className="w-full px-2 py-1 border border-input rounded-md bg-background text-xs">
            <option value="">· none —</option>
            {list.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        ) : <span className="text-muted-foreground">Loading categories…</span>
      ) : (
        <span>{name || (effective ? <code className="text-[10px]">{effective}</code> : <em className="text-muted-foreground">—</em>)}</span>
      )}
    </FieldFrame>
  );
}

// Refund policy — { offset, type }. offset is a free text (Universe enum/ISO
// duration like NEVER / P1D); type is EVENT|PURCHASE.
function RefundPolicyField({ label, fieldKey, value, source, onSave, onReset, busy }) {
  const [editing, setEditing] = useState(false);
  const [offset, setOffset] = useState('');
  const [type, setType] = useState('EVENT');
  const start = () => { setOffset(value?.offset ?? 'NEVER'); setType(value?.type ?? 'EVENT'); setEditing(true); };
  const commit = () => { if (busy) return; onSave({ offset, type }); setEditing(false); };
  return (
    <FieldFrame label={label} fieldKey={fieldKey} source={source} onReset={onReset} busy={busy}
      editing={editing} onEdit={start} onCancel={() => setEditing(false)} onCommit={commit}>
      {editing ? (
        <span className="flex items-center gap-1">
          <input value={offset} onChange={(e) => setOffset(e.target.value)} placeholder="NEVER" title="NEVER, ANYTIME, or an ISO duration like P1D" className="w-28 px-1 py-0.5 border border-input rounded bg-background" />
          <select value={type} onChange={(e) => setType(e.target.value)} className="px-1 py-0.5 border border-input rounded bg-background">
            <option value="EVENT">relative to event</option>
            <option value="PURCHASE">relative to purchase</option>
          </select>
        </span>
      ) : (
        <span>{value ? `${value.offset} (${value.type})` : <em className="text-muted-foreground">—</em>}</span>
      )}
    </FieldFrame>
  );
}

// Taxes — [{ name, percent, rate_ids? }]. Mirrors PUT /multiverse taxes_attributes.
function TaxesField({ value, source, onSave, onReset, busy }) {
  const rows = value || [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(rows);
  useEffect(() => { if (!editing) setDraft(rows); }, [value, editing]); // eslint-disable-line
  const setRow = (i, k, v) => setDraft((d) => d.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const remove = (i) => setDraft((d) => d.filter((_, j) => j !== i));
  const add = () => setDraft((d) => [...d, { name: '', percent: 0, rate_ids: [] }]);
  const save = async () => {
    if (busy) return;
    const clean = draft.filter((r) => r.name && r.name.trim()).map((r) => ({ name: r.name.trim(), percent: Number(r.percent) || 0, rate_ids: r.rate_ids || [] }));
    await onSave(clean.length ? clean : null);
    setEditing(false);
  };
  return (
    <div>
      <CollectionHeader title="Taxes" count={(editing ? draft : rows).length} source={source}
        editing={editing} onAdd={add} addLabel="+ tax" onCancel={() => { setEditing(false); setDraft(rows); }}
        onSave={save} onEdit={() => setEditing(true)} onReset={onReset} busy={busy} />
      {(editing ? draft : rows).length === 0 && !editing ? (
        <div className="text-xs text-muted-foreground italic">Inherits venue taxes.</div>
      ) : (
        <div className="border border-border rounded overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-muted/40 text-left text-muted-foreground"><tr><th className="px-2 py-1">name</th><th className="px-2 py-1 text-right">percent</th>{editing && <th></th>}</tr></thead>
            <tbody>
              {(editing ? draft : rows).map((r, i) => (
                <tr key={i} className="border-t border-border">
                  {editing ? (
                    <>
                      <td className="px-1 py-1"><input value={r.name || ''} onChange={(e) => setRow(i, 'name', e.target.value)} className="w-full px-1 border border-input rounded bg-background" /></td>
                      <td className="px-1 py-1"><input type="number" step="0.01" value={r.percent ?? 0} onChange={(e) => setRow(i, 'percent', e.target.value)} className="w-20 px-1 border border-input rounded bg-background text-right" /></td>
                      <td className="px-1 py-1 text-right"><button onClick={() => remove(i)} className="text-destructive">×</button></td>
                    </>
                  ) : (
                    <><td className="px-2 py-1">{r.name}</td><td className="px-2 py-1 text-right">{r.percent}%</td></>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Rate upgrades (VIP prompts) — override shape is the publisher's push input:
// [{ target_rate_ref, heading, subtitle, description, benefits[], button_text,
//    image_url, enabled }]. target_rate_ref = a LIVE rate name (the push
// resolves it by name; ladder refs also work). The live listing's prompts
// come from /live's juno enrichment (liveUpgrades) — read-only here.
function RateUpgradesField({ value, source, liveUpgrades, liveRates, onSave, onReset, busy }) {
  const rows = value || [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(rows);
  useEffect(() => { if (!editing) setDraft(rows); }, [value, editing]); // eslint-disable-line
  const rateNames = (liveRates || []).map((r) => r.name);
  // When our config has no rows, editing starts FROM THE LIVE PROMPTS — the
  // platform is the source of truth, so the form opens prefilled exactly like
  // Universe's own modal (target + shown-on + content), not empty.
  const liveToRows = () => (liveUpgrades || []).map((u) => ({
    target_rate_ref: u.targetRateName || u.rate_id,
    source_rate_refs: u.sourceRateNames || [],
    heading: u.heading || '', subtitle: u.subtitle || '', description: u.description || '',
    benefits: u.benefits || [], button_text: u.button_text || '',
    image_url: u.image_url || null, enabled: u.enabled !== false,
  }));
  const startEdit = () => { setDraft(rows.length ? rows : liveToRows()); setEditing(true); };
  const setRow = (i, k, v) => setDraft((d) => d.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const remove = (i) => setDraft((d) => d.filter((_, j) => j !== i));
  const add = () => setDraft((d) => [...d, { target_rate_ref: rateNames[0] || '', source_rate_refs: [], heading: '', subtitle: '', description: '', benefits: [], button_text: 'Upgrade Now', enabled: true }]);
  const save = async () => {
    if (busy) return;
    const clean = draft
      .map((r) => ({
        target_rate_ref: String(r.target_rate_ref || '').trim(),
        heading: r.heading || '', subtitle: r.subtitle || '', description: r.description || '',
        benefits: Array.isArray(r.benefits) ? r.benefits.filter(Boolean) : String(r.benefits || '').split('\n').map((b) => b.trim()).filter(Boolean),
        source_rate_refs: (r.source_rate_refs || []).filter(Boolean),
        button_text: r.button_text || '', image_url: r.image_url || null,
        enabled: r.enabled !== false,
      }))
      .filter((r) => r.target_rate_ref);
    await onSave(clean.length ? clean : null);
    setEditing(false);
  };
  return (
    <div>
      <CollectionHeader title="Rate upgrades (VIP prompts)" count={(editing ? draft : rows).length} source={source}
        editing={editing} onAdd={add} addLabel="+ prompt" onCancel={() => { setEditing(false); setDraft(rows); }}
        onSave={save} onEdit={startEdit} onReset={onReset} busy={busy} />
      {(editing ? draft : rows).length === 0 && !editing ? (
        <div className="text-xs text-muted-foreground italic">No upgrade prompts configured locally.</div>
      ) : (
        <div className="space-y-2">
          {(editing ? draft : rows).map((r, i) => editing ? (
            <div key={i} className="border border-border rounded p-2 space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-20">Target rate</span>
                {rateNames.length ? (
                  <select value={r.target_rate_ref || ''} onChange={(e) => setRow(i, 'target_rate_ref', e.target.value)}
                    className="flex-1 px-1 py-0.5 border border-input rounded bg-background">
                    {[...new Set([r.target_rate_ref, ...rateNames].filter(Boolean))].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                ) : (
                  <input value={r.target_rate_ref || ''} onChange={(e) => setRow(i, 'target_rate_ref', e.target.value)} placeholder="live rate name"
                    className="flex-1 px-1 py-0.5 border border-input rounded bg-background" />
                )}
                <label className="inline-flex items-center gap-1 text-muted-foreground">
                  <input type="checkbox" checked={r.enabled !== false} onChange={(e) => setRow(i, 'enabled', e.target.checked)} /> enabled
                </label>
                <button onClick={() => remove(i)} className="text-destructive px-1">×</button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-20">Heading</span>
                <input value={r.heading || ''} onChange={(e) => setRow(i, 'heading', e.target.value)} className="flex-1 px-1 py-0.5 border border-input rounded bg-background" />
                <span className="text-muted-foreground">Button</span>
                <input value={r.button_text || ''} onChange={(e) => setRow(i, 'button_text', e.target.value)} className="w-28 px-1 py-0.5 border border-input rounded bg-background" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-20">Subtitle</span>
                <input value={r.subtitle || ''} onChange={(e) => setRow(i, 'subtitle', e.target.value)} className="flex-1 px-1 py-0.5 border border-input rounded bg-background" />
              </div>
              <div className="flex items-start gap-2">
                <span className="text-muted-foreground w-20 pt-0.5">Benefits</span>
                <textarea rows={3} value={Array.isArray(r.benefits) ? r.benefits.join('\n') : (r.benefits || '')}
                  onChange={(e) => setRow(i, 'benefits', e.target.value)} placeholder="one per line"
                  className="flex-1 px-1 py-0.5 border border-input rounded bg-background" />
              </div>
              <div className="flex items-start gap-2">
                <span className="text-muted-foreground w-20 pt-0.5">Shown on</span>
                <div className="flex-1">
                  <div className="text-[10px] text-muted-foreground mb-1">Which rates display this offer at checkout (none checked = Universe default).</div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 max-h-36 overflow-y-auto border border-border rounded p-1.5">
                    {rateNames.filter((n) => n !== r.target_rate_ref).map((n) => {
                      const sel = (r.source_rate_refs || []).includes(n);
                      return (
                        <label key={n} className="inline-flex items-center gap-1.5 cursor-pointer">
                          <input type="checkbox" checked={sel}
                            onChange={() => setRow(i, 'source_rate_refs', sel
                              ? (r.source_rate_refs || []).filter((x) => x !== n)
                              : [...(r.source_rate_refs || []), n])} />
                          <span className="truncate">{n}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div key={i} className="border border-border rounded p-2 text-xs">
              <div className="font-medium">{r.heading || '(no heading)'} <span className="text-muted-foreground font-normal">→ {r.target_rate_ref}</span></div>
              {(r.source_rate_refs || []).length > 0 && (
                <div className="text-[10px] text-muted-foreground mt-0.5" title={r.source_rate_refs.join(', ')}>Shown on {r.source_rate_refs.length} rate{r.source_rate_refs.length === 1 ? '' : 's'}</div>
              )}
              {(r.benefits || []).length > 0 && <ul className="list-disc ml-4 text-muted-foreground mt-0.5">{r.benefits.map((b, j) => <li key={j}>{b}</li>)}</ul>}
            </div>
          ))}
        </div>
      )}
      {(liveUpgrades || []).length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] text-sky-700 dark:text-sky-300 mb-1">Live on Universe ({liveUpgrades.length})</div>
          <div className="space-y-1">
            {liveUpgrades.map((u) => (
              <div key={u.id} className="border border-sky-500/30 bg-sky-500/5 rounded p-2 text-xs">
                <div className="font-medium">
                  {u.heading || '(no heading)'} <span className="text-muted-foreground font-normal">→ {u.targetRateName || u.rate_id}</span>
                  {!u.enabled && <span className="ml-2 text-[10px] text-muted-foreground">(disabled)</span>}
                  {u.button_text && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15">{u.button_text}</span>}
                </div>
                {(u.sourceRateNames || []).length > 0 && (
                  <div className="text-[10px] text-muted-foreground mt-0.5" title={u.sourceRateNames.join(', ')}>
                    Shown on {u.sourceRateNames.length} rate{u.sourceRateNames.length === 1 ? '' : 's'}: {u.sourceRateNames.slice(0, 3).join(', ')}{u.sourceRateNames.length > 3 ? ` +${u.sourceRateNames.length - 3} more` : ''}
                  </div>
                )}
                {(u.benefits || []).length > 0 && <ul className="list-disc ml-4 text-muted-foreground mt-0.5">{u.benefits.map((b, j) => <li key={j}>{b}</li>)}</ul>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Pixel lists (Facebook / TikTok) — [{ pixel: "<id>" }], the shape the
// resolver's trackingAttrs pushes via eventUpdate.
function PixelListField({ title, value, source, onSave, onReset, busy }) {
  const rows = value || [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(rows);
  useEffect(() => { if (!editing) setDraft(rows); }, [value, editing]); // eslint-disable-line
  const setRow = (i, v) => setDraft((d) => d.map((r, j) => j === i ? { ...r, pixel: v } : r));
  const remove = (i) => setDraft((d) => d.filter((_, j) => j !== i));
  const add = () => setDraft((d) => [...d, { pixel: '' }]);
  const save = async () => {
    if (busy) return;
    const clean = draft.map((r) => ({ pixel: String(r.pixel || '').trim() })).filter((r) => r.pixel);
    await onSave(clean.length ? clean : null);
    setEditing(false);
  };
  return (
    <div>
      <CollectionHeader title={title} count={(editing ? draft : rows).length} source={source}
        editing={editing} onAdd={add} addLabel="+ pixel" onCancel={() => { setEditing(false); setDraft(rows); }}
        onSave={save} onEdit={() => setEditing(true)} onReset={onReset} busy={busy} />
      {(editing ? draft : rows).length === 0 && !editing ? (
        <div className="text-xs text-muted-foreground italic">None configured.</div>
      ) : (
        <div className="space-y-1">
          {(editing ? draft : rows).map((r, i) => editing ? (
            <div key={i} className="flex items-center gap-1">
              <input value={r.pixel || ''} onChange={(e) => setRow(i, e.target.value)} placeholder="pixel ID"
                className="flex-1 px-2 py-1 text-xs border border-input rounded bg-background font-mono" />
              <button onClick={() => remove(i)} className="text-destructive text-xs px-1">×</button>
            </div>
          ) : (
            <code key={i} className="block text-[11px]">{r.pixel}</code>
          ))}
        </div>
      )}
    </div>
  );
}

// Google Ads trackers — [{ type: 'site_tag' | 'purchase', sendTo: 'AW-…' }].
const ADS_TRACKER_TYPES = ['site_tag', 'purchase'];
function AdsTrackersField({ value, source, onSave, onReset, busy }) {
  const rows = value || [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(rows);
  useEffect(() => { if (!editing) setDraft(rows); }, [value, editing]); // eslint-disable-line
  const setRow = (i, k, v) => setDraft((d) => d.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const remove = (i) => setDraft((d) => d.filter((_, j) => j !== i));
  const add = () => setDraft((d) => [...d, { type: 'site_tag', sendTo: '' }]);
  const save = async () => {
    if (busy) return;
    const clean = draft
      .map((r) => ({ type: r.type || 'site_tag', sendTo: String(r.sendTo || '').trim() }))
      .filter((r) => r.sendTo);
    await onSave(clean.length ? clean : null);
    setEditing(false);
  };
  return (
    <div>
      <CollectionHeader title="Google Ads trackers" count={(editing ? draft : rows).length} source={source}
        editing={editing} onAdd={add} addLabel="+ tracker" onCancel={() => { setEditing(false); setDraft(rows); }}
        onSave={save} onEdit={() => setEditing(true)} onReset={onReset} busy={busy} />
      {(editing ? draft : rows).length === 0 && !editing ? (
        <div className="text-xs text-muted-foreground italic">None configured.</div>
      ) : (
        <div className="border border-border rounded overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-muted/40 text-left text-muted-foreground"><tr><th className="px-2 py-1">type</th><th className="px-2 py-1">send to</th>{editing && <th></th>}</tr></thead>
            <tbody>
              {(editing ? draft : rows).map((r, i) => (
                <tr key={i} className="border-t border-border">
                  {editing ? (
                    <>
                      <td className="px-1 py-1">
                        <select value={r.type || 'site_tag'} onChange={(e) => setRow(i, 'type', e.target.value)}
                          className="px-1 py-0.5 border border-input rounded bg-background">
                          {[...new Set([...ADS_TRACKER_TYPES, r.type].filter(Boolean))].map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td className="px-1 py-1"><input value={r.sendTo || ''} onChange={(e) => setRow(i, 'sendTo', e.target.value)} placeholder="AW-…"
                        className="w-full px-1 border border-input rounded bg-background font-mono" /></td>
                      <td className="px-1 py-1 text-right"><button onClick={() => remove(i)} className="text-destructive">×</button></td>
                    </>
                  ) : (
                    <><td className="px-2 py-1">{r.type}</td><td className="px-2 py-1 font-mono">{r.sendTo}</td></>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Fees — [{ name, flat, percent }]. Mirrors cost_attributes.fees_attributes.
function FeesField({ value, source, onSave, onReset, busy }) {
  const rows = value || [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(rows);
  useEffect(() => { if (!editing) setDraft(rows); }, [value, editing]); // eslint-disable-line
  const setRow = (i, k, v) => setDraft((d) => d.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const remove = (i) => setDraft((d) => d.filter((_, j) => j !== i));
  const add = () => setDraft((d) => [...d, { name: '', flat: 0, percent: 0 }]);
  const save = async () => {
    if (busy) return;
    const clean = draft.filter((r) => r.name && r.name.trim()).map((r) => ({ name: r.name.trim(), flat: Number(r.flat) || 0, percent: Number(r.percent) || 0 }));
    await onSave(clean.length ? clean : null);
    setEditing(false);
  };
  return (
    <div>
      <CollectionHeader title="Fees" count={(editing ? draft : rows).length} source={source}
        editing={editing} onAdd={add} addLabel="+ fee" onCancel={() => { setEditing(false); setDraft(rows); }}
        onSave={save} onEdit={() => setEditing(true)} onReset={onReset} busy={busy} />
      {(editing ? draft : rows).length === 0 && !editing ? (
        <div className="text-xs text-muted-foreground italic">Inherits venue fees.</div>
      ) : (
        <div className="border border-border rounded overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-muted/40 text-left text-muted-foreground"><tr><th className="px-2 py-1">name</th><th className="px-2 py-1 text-right">flat $</th><th className="px-2 py-1 text-right">percent</th>{editing && <th></th>}</tr></thead>
            <tbody>
              {(editing ? draft : rows).map((r, i) => (
                <tr key={i} className="border-t border-border">
                  {editing ? (
                    <>
                      <td className="px-1 py-1"><input value={r.name || ''} onChange={(e) => setRow(i, 'name', e.target.value)} className="w-full px-1 border border-input rounded bg-background" /></td>
                      <td className="px-1 py-1"><input type="number" step="0.01" value={r.flat ?? 0} onChange={(e) => setRow(i, 'flat', e.target.value)} className="w-20 px-1 border border-input rounded bg-background text-right" /></td>
                      <td className="px-1 py-1"><input type="number" step="0.01" value={r.percent ?? 0} onChange={(e) => setRow(i, 'percent', e.target.value)} className="w-20 px-1 border border-input rounded bg-background text-right" /></td>
                      <td className="px-1 py-1 text-right"><button onClick={() => remove(i)} className="text-destructive">×</button></td>
                    </>
                  ) : (
                    <><td className="px-2 py-1">{r.name}</td><td className="px-2 py-1 text-right">${r.flat ?? 0}</td><td className="px-2 py-1 text-right">{r.percent ?? 0}%</td></>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// === raw JSON fallback (only for array/object shapes without a dedicated editor) ===

function RawJsonField({ label, fieldKey, value, source, onSave, onReset, busy }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [parseErr, setParseErr] = useState(null);
  const start = () => { setDraft(value == null ? '' : JSON.stringify(value, null, 2)); setParseErr(null); setEditing(true); };
  const commit = () => {
    if (busy) return;
    let parsed = draft;
    try { parsed = draft.trim() === '' ? null : JSON.parse(draft); }
    catch (e) { setParseErr(`Invalid JSON: ${e.message}`); return; }
    onSave(parsed);
    setEditing(false);
  };
  const overridden = source === 'event';
  return (
    <div className="py-2 border-t border-border first:border-0 text-xs">
      <div className="flex items-center justify-between mb-1">
        <div>
          <span className="font-medium">{label || fieldKey}</span>
          <span className="text-[10px] text-muted-foreground font-mono ml-2">{fieldKey} · advanced (raw)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <SourceBadge source={source} />
          {!editing && overridden && onReset && <ResetButton onReset={onReset} busy={busy} />}
          {editing ? (
            <>
              <button onClick={commit} disabled={busy} aria-disabled={busy} className="text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded disabled:opacity-50">{busy ? 'saving…' : 'save'}</button>
              <button onClick={() => setEditing(false)} className="text-[10px] px-1.5 py-0.5 border border-input rounded">cancel</button>
            </>
          ) : (
            <button onClick={() => { if (!busy) start(); }} disabled={busy} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-50">edit</button>
          )}
        </div>
      </div>
      {editing ? (
        <>
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={Math.min(14, Math.max(4, (draft.match(/\n/g) || []).length + 2))} className="w-full px-2 py-1 border border-input rounded bg-background font-mono text-[11px]" />
          {parseErr && <div className="text-[10px] text-destructive mt-1">{parseErr}</div>}
        </>
      ) : (
        <code className="text-[10px] text-muted-foreground break-all">{value == null ? '—' : JSON.stringify(value).slice(0, 200)}{value != null && JSON.stringify(value).length > 200 ? '…' : ''}</code>
      )}
    </div>
  );
}

// === UrVenue performers (live-direct: attach/remove hit the platform) ======
function UrvPerformersField({ live, ctx, busy }) {
  const [performers, setPerformers] = useState(live?.performers || []);
  useEffect(() => { setPerformers(live?.performers || []); }, [live]);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [working, setWorking] = useState(false);
  const [err, setErr] = useState(null);

  const refresh = async () => {
    try {
      const r = await apiFetch(`/api/tenants/${ctx.tenantSlug}/${ctx.platform}/events/${ctx.eventId}/live`);
      const d = await r.json();
      if (r.ok && d.published) setPerformers(d.performers || []);
    } catch { /* best-effort */ }
  };
  const search = async () => {
    if (!q.trim()) return;
    setSearching(true); setErr(null);
    try {
      const r = await apiFetch(`/api/tenants/${ctx.tenantSlug}/${ctx.platform}/performers/search?q=${encodeURIComponent(q.trim())}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || 'search_failed');
      setResults(d.artists || []);
    } catch (e) { setErr(e.message); }
    finally { setSearching(false); }
  };
  const attach = async (artist, importance) => {
    setWorking(true); setErr(null);
    try {
      const r = await apiFetch(`/api/tenants/${ctx.tenantSlug}/${ctx.platform}/events/${ctx.eventId}/performers?authorize_writes=1`, {
        method: 'POST', body: JSON.stringify({ artist, importance }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || 'add_failed');
      await refresh();
    } catch (e) { setErr(e.message); }
    finally { setWorking(false); }
  };
  const remove = async (eventdjid) => {
    setWorking(true); setErr(null);
    try {
      const r = await apiFetch(`/api/tenants/${ctx.tenantSlug}/${ctx.platform}/events/${ctx.eventId}/performers/remove?authorize_writes=1`, {
        method: 'POST', body: JSON.stringify({ eventdjid }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || 'remove_failed');
      await refresh();
    } catch (e) { setErr(e.message); }
    finally { setWorking(false); }
  };

  if (!live?.published) {
    return <div className="text-xs text-muted-foreground italic py-2">Performers attach to the live UrVenue event. Publish first.</div>;
  }
  return (
    <div className="space-y-3 py-2">
      <div className="text-[10px] uppercase text-muted-foreground">Performers ({performers.length}) <span className="normal-case">· edits apply to UrVenue immediately</span></div>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="block text-xs text-muted-foreground mb-1">Search the venue's artist directory</label>
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()}
            placeholder="artist name" className="w-full px-2 py-1 text-xs border border-input rounded-md bg-background" />
        </div>
        <button onClick={search} disabled={searching || !q.trim()}
          className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50">{searching ? 'Searching…' : 'Search'}</button>
      </div>
      {err && <div className="text-xs text-destructive">{err}</div>}
      {results && (
        <div className="border border-border rounded-md max-h-48 overflow-y-auto">
          {results.length === 0 && <div className="text-xs text-muted-foreground p-2">No matches in the directory.</div>}
          {results.map((a) => {
            const already = performers.some((pf) => String(pf.artistid) === String(a.id));
            return (
              <div key={a.id} className="flex items-center justify-between px-2 py-1.5 border-t border-border first:border-0 text-xs">
                <div className="min-w-0">
                  <div className="font-medium truncate">{a.name}</div>
                  <div className="text-muted-foreground text-[10px] truncate">{a.artisttype}{a.realname ? ` · ${a.realname}` : ''}</div>
                </div>
                {already ? <span className="text-[10px] text-muted-foreground">attached</span> : (
                  <div className="flex gap-1">
                    <button onClick={() => attach(a, 1)} disabled={working} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-50">attach</button>
                    <button onClick={() => attach(a, 2)} disabled={working} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-50" title="Attach as headliner">★ headliner</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase text-muted-foreground mb-1">Attached ({performers.length})</div>
        {performers.length === 0 ? (
          <div className="text-xs text-muted-foreground italic">No performers on the live event.</div>
        ) : (
          <div className="space-y-1">
            {performers.map((pf) => (
              <div key={pf.id} className="flex items-center justify-between border border-border rounded p-2 text-xs">
                <div className="font-medium">{pf.importance === 2 && <span className="text-amber-500 mr-1">★</span>}{pf.name}</div>
                <button onClick={() => remove(pf.id)} disabled={working}
                  className="text-[10px] px-1.5 py-0.5 border border-input rounded text-destructive hover:bg-destructive hover:text-destructive-foreground disabled:opacity-50">remove</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// === dispatcher ============================================================
// Picks the editor for a catalog entry. `meta.key` wins for the dedicated
// collection editors; otherwise `meta.kind` selects the widget.
export function OverrideField({ meta, value, source, resolved, sources, event, live, ctx, patch, busy, onPromote, staged, promotable }) {
  const onSave = (v) => patch(meta.key, v);
  const onReset = () => patch(meta.key, null);
  // Live value for this field (what's actually on the platform right now).
  const liveValue = live?.scalars ? live.scalars[meta.key] : undefined;

  // Promote targets for this field: venue defaults when the key has a venue
  // slot, template only when the event uses one. Promotes OUR effective value
  // when set, else the live value (the "capture what's on the platform" case).
  const promote = (() => {
    if (!onPromote) return null;
    // Per-platform promotable keys; falls back to the Universe sets.
    const venueSet = promotable?.venue ? new Set(promotable.venue) : VENUE_PROMOTABLE;
    const templateSet = promotable?.template ? new Set(promotable.template) : TEMPLATE_PROMOTABLE;
    const targets = [];
    if (templateSet.has(meta.key) && event?.template_id) targets.push('template');
    if (venueSet.has(meta.key)) targets.push('venue');
    const promoteVal = (value !== undefined && value !== null && value !== '') ? value : liveValue;
    if (!targets.length || promoteVal === undefined || promoteVal === null || promoteVal === '') return null;
    return { targets, run: (scope) => onPromote(meta.key, scope, promoteVal) };
  })();

  // Dedicated collection editors keyed by field name.
  switch (meta.key) {
    case 'rateLadder':     return <RateLadderField value={value} source={source} liveRates={live?.rates} onSave={onSave} onReset={onReset} busy={busy} />;
    case 'hostFields':     return <HostFieldsField value={value} source={source} liveHostFields={live?.hostFields} onSave={onSave} onReset={onReset} busy={busy} />;
    case 'discountCodes':  return <DiscountsField value={value} source={source} onSave={onSave} onReset={onReset} busy={busy} />;
    case 'addOns':         return <AddOnsField value={value} source={source} onSave={onSave} onReset={onReset} busy={busy} />;
    case 'emailTemplates': return <EmailTemplatesField value={value} source={source} onSave={onSave} onReset={onReset} busy={busy} />;
    case 'eventPhoto':
    case 'coverPhoto':
    case 'additionalPhotos': return <ImagesField resolved={resolved} sources={sources} live={live} patch={patch} busy={busy} />;
    case 'attractions':    return <AttractionsField resolved={resolved} event={event} ctx={ctx} patch={patch} busy={busy} />;
    case 'rateUpgrades':   return <RateUpgradesField value={value} source={source} liveUpgrades={live?.rateUpgrades} liveRates={live?.rates} onSave={onSave} onReset={onReset} busy={busy} />;
    case 'facebookPixels': return <PixelListField title="Facebook pixels" value={value} source={source} onSave={onSave} onReset={onReset} busy={busy} />;
    case 'tiktokPixels':   return <PixelListField title="TikTok pixels" value={value} source={source} onSave={onSave} onReset={onReset} busy={busy} />;
    case 'googleAdsTrackers': return <AdsTrackersField value={value} source={source} onSave={onSave} onReset={onReset} busy={busy} />;
    case 'taxes':          return <TaxesField value={value} source={source} onSave={onSave} onReset={onReset} busy={busy} />;
    case 'fees':           return <FeesField value={value} source={source} onSave={onSave} onReset={onReset} busy={busy} />;
    case 'refundPolicy':   return <RefundPolicyField label={meta.label} fieldKey={meta.key} value={value} source={source} onSave={onSave} onReset={onReset} busy={busy} />;
    case 'categoryId':     return <CategoryField label={meta.label} fieldKey={meta.key} value={value} source={source} ctx={ctx} onSave={onSave} onReset={onReset} busy={busy} />;
    case 'event_type_id':  return <CategoryField label={meta.label} fieldKey={meta.key} value={value} source={source} ctx={ctx} liveValue={liveValue} onSave={onSave} onReset={onReset} busy={busy} />;
    case 'inventory_template_id': return <CategoryField label={meta.label} fieldKey={meta.key} value={value} source={source} ctx={ctx} optionsPath="inventory-templates" liveValue={liveValue} onSave={onSave} onReset={onReset} busy={busy} />;
    case 'performers':     return <UrvPerformersField live={live} ctx={ctx} busy={busy} />;
    default: break;
  }

  // Widget by kind.
  switch (meta.kind) {
    case 'html':   return <HtmlField staged={staged} label={meta.label} fieldKey={meta.key} value={value} source={source} liveValue={liveValue} onSave={onSave} onReset={onReset} busy={busy} promote={promote} />;
    case 'bool':   return <BoolField staged={staged} label={meta.label} fieldKey={meta.key} value={value} source={source} liveValue={liveValue} onSave={onSave} onReset={onReset} busy={busy} promote={promote} />;
    case 'number': return <ScalarField staged={staged} label={meta.label} fieldKey={meta.key} value={value} source={source} liveValue={liveValue} kind="number" onSave={onSave} onReset={onReset} busy={busy} promote={promote} />;
    case 'datetime': return <DateTimeFieldRow staged={staged} label={meta.label} fieldKey={meta.key} value={value} source={source} liveValue={liveValue} tz={event?.venue_timezone} onSave={onSave} onReset={onReset} busy={busy} promote={promote} />;
    case 'time':   return <TimeFieldRow staged={staged} label={meta.label} fieldKey={meta.key} value={value} source={source} liveValue={liveValue} format={meta.timeFormat || 'HH:MM'} onSave={onSave} onReset={onReset} busy={busy} promote={promote} />;
    case 'date':   return <DateFieldRow staged={staged} label={meta.label} fieldKey={meta.key} value={value} source={source} liveValue={liveValue} onSave={onSave} onReset={onReset} busy={busy} promote={promote} />;
    case 'duration': return <DurationFieldRow staged={staged} label={meta.label} fieldKey={meta.key} value={value} source={source} liveValue={liveValue} relLabel={meta.durationRel} onSave={onSave} onReset={onReset} busy={busy} promote={promote} />;
    case 'text':   return <ScalarField staged={staged} label={meta.label} fieldKey={meta.key} value={value} source={source} liveValue={liveValue} kind="text" onSave={onSave} onReset={onReset} busy={busy} promote={promote} />;
    default:       return <RawJsonField label={meta.label} fieldKey={meta.key} value={value} source={source} onSave={onSave} onReset={onReset} busy={busy} />;
  }
}

// Renders a subtle "live: X" line when the platform's current value differs
// from ours — so ops sees what's actually live while editing. Stays visible
// when our side is unset but live has a value (the "missing from our tiers"
// signal that feeds the promote buttons).
function LiveLine({ liveValue, value }) {
  if (liveValue === undefined || liveValue === null || liveValue === '') return null;
  if (Array.isArray(liveValue) && liveValue.length === 0) return null; // empty list = platform "no value"
  if (liveEq(liveValue, value)) return null;
  const isBool = typeof liveValue === 'boolean';
  const isObj = !isBool && typeof liveValue === 'object';
  const txt = isBool ? (liveValue ? 'on' : 'off')
    : isObj ? JSON.stringify(liveValue).slice(0, 80)
    : String(liveValue);
  return (
    <div className="text-[10px] text-sky-700 dark:text-sky-300 mt-1">
      live on platform: {isObj ? <code>{txt}</code> : <span className="text-foreground/80">{txt}</span>}
    </div>
  );
}
function jsonEqLoose(a, b) {
  if (a === b) return true;
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}
// Type-normalized live-vs-ours equality, so formatting alone never flags a
// difference: booleans coerce (an unset toggle ≡ false ≡ "off"), numerics
// compare as numbers ("0" ≡ 0), strings compare canonicalized. String canon
// also erases Universe's HTML-sanitizer rewrites (rel="nofollow" injected
// into links, & escaped to &amp;) so a pushed description doesn't phantom-
// flag forever against its own sanitized echo. Keep in sync with canonStr/
// normEq in server/routes/universe.js.
const toBool = (v) => v === true || v === 'true';
function canonStr(v) {
  let s = String(v).replace(/\s+rel="[^"]*"/gi, '');
  let prev;
  do { prev = s; s = s.replace(/&amp;/g, '&'); } while (s !== prev);
  s = s.replace(/\s+/g, ' ').trim();
  // Clock times compare without seconds ("23:30:00" ≡ "23:30").
  const t = s.match(/^(\d{1,2}:\d{2}):00$/);
  if (t) s = t[1];
  return s;
}
function liveEq(live, ours) {
  if (jsonEqLoose(live, ours)) return true;
  if (typeof live === 'boolean' || typeof ours === 'boolean') {
    return toBool(live) === toBool(ours);
  }
  if (typeof live !== 'object' && ours != null && typeof ours !== 'object') {
    const a = canonStr(live);
    const b = canonStr(ours);
    if (a === b) return true;
    if (a !== '' && b !== '' && !Number.isNaN(Number(a)) && !Number.isNaN(Number(b))) {
      return Number(a) === Number(b);
    }
  }
  return false;
}

// Keys whose UI is fully owned by another field's editor, so the shell must
// not render a standalone row for them.
export const FIELD_RENDERED_ELSEWHERE = new Set(['coverPhoto', 'additionalPhotos', 'individualAttractions']);
